import { useEffect, useRef, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { DECREES, type Command, type Resource } from '@aurane/protocol';
import { AGENT_COST_INFLUENCE, AMPLIFIER_RANGE_MULT, BASE_PRICE, MAKER_BUY_MULT, MAKER_QTY, MAKER_SELL_MULT, BASE_RANGE, BEACON_RANGE_MULT, BUILDING_ORBIT, DECREE_COST_CREDITS, DECREE_HOURS, DECREE_RANGE_MULT, PULSAR_RANGE_MULT, RELAY_BUILD_SECONDS_PER_UNIT, RELAY_COST_PER_UNIT, STORM_RANGE_MULT, counselLine, counselTitle, nextStepParams, nextTierLine, type PlayerView, type ShowTarget, type SystemView } from '@aurane/sim';
import { GalaxyMap } from '../map/GalaxyMap.js';
import { act, addPasskey, answerCounsel, confirmDoctrine, discardDoctrine, eraseMemory, exportMemory, fetchAccount, fetchBriefing, fetchCounsel, fetchEntitlements, fetchPendingDoctrine, fetchPublicConfig, fetchSessions, fetchTalk, fetchArchives, fetchProjection, type ProjectionView, type ArchivesView, openSeance as apiOpenSeance, sayInSeance, closeSeance as apiCloseSeance, fetchSeance, type SeanceView, logout, paidReturn, startCheckout, type PaymentsConfig, passkeysSupported, removeEmail, removePasskey, setNotifyEmail, authHeaders, fetchPushConfig, revokeSession, startEmail, status, talk as sendTalk, toast, verifyEmail, view, requestLink, type AccountInfo, type CounselCard, type CounselView, type SessionInfo, type Turn } from '../net.js';
import { lang, setLang, t, tError } from '../i18n/index.js';
import { useSig } from './useSig.js';
import { Icon } from './Icon.js';
import { SystemMode, orderLabel, probeOpen, unitLine } from './SystemView.js';
import { ExpeditionBanner, ExpeditionHost } from './ExpeditionEntry.js';
import { expeditionOpen, expeditionsOn, loadExpeditions, rtPrep } from './expeditionStore.js';
import { LogisticsPanel } from './Logistics.js';
import { InstallButton, RES, UpdateBanner, fmt, hms } from './bits.js';
import { decreeLabel, describeEvent, describeNote, etaText, fill, stamp } from './feed.js';
import { minutesText, netStatus } from './netstatus.js';
import { CARD_LINES, awaitingAnswer, composerPlaceholderKey, cardAfterTalk, cardFromPending, pendingLineKey, threadItems, visibleLines, type PendingCard } from './doctrine.js';
import { doneCards, doneThisDraw, objectiveText, type ShownCard } from './counsel.js';
import { MapLegend } from './MapLegend.js';
import { AncientRelays, BeaconControls } from './PharesBits.js';
import { countPlayed, drawPlan, momentPlan, newMoments, playedCounts, prefersReducedMotion, type Moment, type MomentPlan } from './moments.js';
import { marketPrefill, pendingDemo, pointAt, sceneFlashReq, stopTeaching, teach, teachClass, teachEvent, teachKey, watchScreen } from './teach.js';
import { disablePush, enablePush, pushState, type PushState } from '../pwa.js';
import { installDecisionFx, decision } from './decisionFx.js';
import { Constellation, Engraved, RecallFlash, noteCounselTaken, recallLine, setCounselTaken } from './generalFx.js';

type Tab = 'colony' | 'system' | 'logistics' | 'market' | 'fleets' | 'diplomacy' | 'general' | 'plan' | 'log' | 'account';
type TplKey = 'tplForge' | 'tplOasis' | 'tplCrossroads' | 'tplGraveyard' | 'tplSanctuary' | 'tplLair' | 'tplBurnt';
const TPL_KEY: Record<string, TplKey> = { forge: 'tplForge', oasis: 'tplOasis', crossroads: 'tplCrossroads', graveyard: 'tplGraveyard', sanctuary: 'tplSanctuary', lair: 'tplLair', burnt: 'tplBurnt' };
const selected = signal<string | null>(null);
const linkFrom = signal<string | null>(null);
const LINK_MODE_MS = 45000;
// Back from Stripe's checkout: straight to the Account tab, where the thanks and the title wait.
const tab = signal<Tab>(paidReturn.value ? 'account' : 'colony');
const briefing = signal<{ text: string; source: string } | null>(null);
/** The system whose plateau is open full-screen, or null for the galaxy. */
export const systemMode = signal<string | null>(null);
// #debug (see net.ts): open a System by its id, for the visual checks and the end-to-end run of the expedition.
if (typeof location !== 'undefined' && location.hash === '#debug') { const dbg = (globalThis as unknown as { __aurane?: Record<string, unknown> }).__aurane; if (dbg) dbg.openSystem = (id: string) => { systemMode.value = id; }; }
/** Bumped when something asks the panel to unfold (the Counsel's "Show me" on a phone), or to fold so the map shows. */
const openPanel = signal(paidReturn.value ? 1 : 0);
const foldPanel = signal(0);
/** « The screen » a hint of the General was said on: the tab, the system open full-screen, the link mode. */
const screenKey = (): string => `${tab.value}|${systemMode.value ?? ''}|${linkFrom.value ?? ''}`;
watchScreen(screenKey);

export function Game() {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<GalaxyMap | null>(null);
  const v = useSig(view)!;
  const st = useSig(status);
  const toastV = useSig(toast);
  const linkV = useSig(linkFrom);
  const selV = useSig(selected);
  const sysMode = useSig(systemMode);

  useEffect(() => {
    const m = new GalaxyMap({
      onSelect: (id) => {
        if (linkFrom.value && id && id !== linkFrom.value) {
          const from = linkFrom.value;
          linkFrom.value = null;
          void act({ type: 'build_relay', a: from, b: id });
          selected.value = id;
          tab.value = 'system';
          return;
        }
        selected.value = id;
        if (id) { tab.value = 'system'; starTapped.value++; } else emptyTapped.value++;
      },
      insets: () => mapInsets(host.current),
    });
    map.current = m;
    installDecisionFx(() => map.current);
    void fetchPublicConfig().then((c) => m.setSeasonDays(c.seasonDays)).catch(() => {});
    if (typeof location !== 'undefined' && location.hash === '#debug') {
      const dbg = (globalThis as unknown as { __aurane?: Record<string, unknown> }).__aurane;
      if (dbg) {
        dbg.flyTo = (id: string) => { m.setSelection(id); m.flyTo(id, 0); };
        // capture and QA of the Signal's effects (signalFx, decisionFx): replay a lit relay, place the Silence, an order
        dbg.replayLight = (a: string, b: string) => m.replayLight(a, b);
        dbg.silence = (p: number | null) => m.setSilence(p);
        dbg.ack = (id: string) => m.ack(id);
        dbg.decision = (x: number, y: number, id: string) => decision({ x, y }, id);
        dbg.seance = (sv: SeanceView | null) => { seanceV.value = sv; };
        dbg.zoom = (k: number) => m.zoomTo(k);
        dbg.look = (x: number, y: number, k: number) => m.lookAt(x, y, k);
        dbg.beat = () => m.beatNow();
        dbg.moment = (system: string, path: string[]) => playMoment({ key: `dbg:${Date.now()}`, kind: 'relay', system, at: Date.now(), path }, true);
        dbg.openPrep = (system: string, poi: string) => { rtPrep.value = { system, poi }; };
      }
    }
    void m.mount(host.current!).then(() => m.update(view.value!));
    return () => m.destroy();
  }, []);

  useEffect(() => { map.current?.update(v); }, [v]);
  // The moments (issue #48): a relay that lights up, a first contact. What happened before this screen opened is not
  // replayed (the Journal says it); what happens while it is open plays, one at a time.
  useEffect(() => {
    momentMap = map.current;
    // A memory that comes back (decision 0012) waits for the moment on screen, then stays until the player closes it.
    for (const e of v.events) {
      if (e.kind !== 'souvenir.recovered' || e.actors[0] !== v.me.id) continue;
      const d = e.data as { id?: string; title?: { fr: string; en: string }; text?: { fr: string; en: string } } | undefined;
      if (!d?.id || !d.title || !d.text || souvenirShown().has(d.id) || souvenirQueue.some((x) => x.id === d.id)) continue;
      souvenirQueue.push({ id: d.id, title: d.title, text: d.text });
    }
    if (!souvenirNow.value && souvenirQueue.length) souvenirNow.value = souvenirQueue.shift()!;
    const marks = v.events.filter((e) => e.actors[0] === v.me.id && (e.kind === 'echo.caught' || e.kind === 'souvenir.recovered' || e.kind === 'souvenir.hint')).length;
    if (marks !== archivesMarks) { archivesMarks = marks; void refreshArchives(); }
    if (!momentsSeen) { momentsSeen = new Set(newMoments(v, new Set()).map((m) => m.key)); return; }
    for (const m of newMoments(v, momentsSeen)) { momentsSeen.add(m.key); momentQueue.push(m); }
    if (!momentNow.value) nextMoment();
  }, [v]);
  const mom = useSig(momentNow);
  const souv = useSig(souvenirNow);
  const postV = useSig(post);
  const narrowV = useSig(narrow);
  const capture = useSig(captureMode);
  // The absence report only when there is something to report (playtest 28.09): a Draw came and went, or an hour away.
  // Expeditions (decision 0015): whether the server offers them, and one under way to resume.
  useEffect(() => { void loadExpeditions(); }, []);
  useEffect(() => { void fetchBriefing(lang.value).then((b) => { if (b && b.awaySeconds >= 600 && ((b.draws ?? 1) >= 1 || b.awaySeconds >= 3600)) briefing.value = b; }); }, []);
  const brief = useSig(briefing);
  const xpOpen = useSig(expeditionOpen);
  useEffect(() => { map.current?.setSelection(selV); }, [selV]);
  useEffect(() => { map.current?.setLinkFrom(linkV); }, [linkV]);
  // The link mode is a lesson, not a state to live in: it closes itself if no star is tapped for a while (issue #33).
  useEffect(() => {
    if (!linkV) return;
    const id = setTimeout(() => { if (linkFrom.value === linkV) linkFrom.value = null; }, LINK_MODE_MS);
    return () => clearTimeout(id);
  }, [linkV]);
  // The General points at a button: bring it into view; the lesson ends when the player taps it.
  const teachV = useSig(teach);
  useEffect(() => {
    if (!teachV || teachV.done || !teachV.key) return;
    const id = setTimeout(() => document.querySelector('.teach')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 400);
    return () => clearTimeout(id);
  }, [teachV]);
  // Another screen chosen, or the link mode it opened cancelled: the hint is over (issue #39).
  const tabV = useSig(tab);
  useEffect(() => { teachEvent({ kind: 'screen', screen: screenKey() }); }, [tabV, sysMode, linkV]);
  const onTap = (e: MouseEvent): void => { if ((e.target as HTMLElement | null)?.closest?.('.teach')) setTimeout(stopTeaching, 150); };

  return (
    <div class={`game ${capture ? 'capture' : ''} ${postV} ${narrowV ? 'narrow' : ''}`} onClickCapture={onTap}>
      <div class="map" ref={host} />
      {postV === 'table' && !capture && <Dock v={v} map={map} />}
      {!sysMode && <MapLegend />}
      <Hud v={v} map={map} />
      {st !== 'online' && <div class="banner">{t('offline')}</div>}
      {toastV && <div class={`toast ${toastV.kind}`}>{toastV.text}</div>}
      {mom && (
        <div class={`moment ${mom.plan.camera ? '' : 'still'} ${mom.kind === 'draw' ? '' : 'low'}`} role="status" onClick={skipMoment} style={{ animationDuration: `${mom.plan.titleMs + mom.plan.cameraMs}ms` }}>
          <b>{mom.title}</b>
          {mom.kind === 'draw' && <span class="bands">{(mom.bands ?? []).map((b, i) => <i key={b} style={{ animationDelay: `${300 + i * 700}ms` }}>{b}</i>)}</span>}
          {mom.kind === 'draw' && mom.gazette && <a class="gz" href="/gazette" target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>{t('gazette')} · {mom.gazette}</a>}
          {!capture && <small>{t('momentSkip')}</small>}
        </div>
      )}
      <SeanceOverlay v={v} />
      {/* QA 11.10: never over the absence report (it covered « Compris ») nor over an expedition: it waits for them. */}
      {souv && !mom && !capture && !seanceV.value && !brief && !xpOpen && postV !== 'bridge' && <SouvenirCard v={v} />}
      {capture && <button class="capture-exit" aria-label={t('captureExit')} onClick={exitCapture} />}
      {linkV && !teachV && <div class="hint">{t('tapToLink')} <button onClick={() => { linkFrom.value = null; }}>{t('cancel')}</button></div>}
      {teachV && <div class={`hint teachbar ${teachV.done ? 'done' : ''}`}><span>{teachV.hint}</span><button onClick={stopTeaching}>✕</button></div>}
      {brief && (
        <div class="briefing">
          <h3>{t('briefing')} · {t(v.me.persona as 'vane')}</h3>
          <pre>{brief.text}</pre>
          <button class="primary" onClick={() => { briefing.value = null; }}>{t('dismiss')}</button>
        </div>
      )}
      <Panel v={v} map={map} />
      {sysMode && <SystemMode v={v} systemId={sysMode} onLeave={() => leaveSystem(sysMode, map.current)} />}
      <ExpeditionHost />
    </div>
  );
}

/** Back to the galaxy from a plateau. On a phone (Nick, 11.10: « on y comprend rien ») the sheet of the system just left
 *  covered the map with the HUD: the panel folds and the map shows the star the player comes from, its sheet one tap away. */
function leaveSystem(id: string, map: GalaxyMap | null): void {
  systemMode.value = null;
  selected.value = id;
  tab.value = 'system';
  if (!isNarrow()) return;
  foldPanel.value++;
  // After the fold's transition, so the frame is computed on the map left free.
  setTimeout(() => { map?.centerOn(id); map?.flash(id); }, 260);
}

/** Live alerts: fights and blockades first, then hostile fleets on their way (with the hour of arrival), then what waits for an answer. */
function Alerts({ v }: { v: PlayerView }) {
  const [, force] = useState(0);
  useEffect(() => { const i = setInterval(() => force((x) => x + 1), 30000); return () => clearInterval(i); }, []);
  const name = (id: string): string => v.colonies.find((c) => c.id === id)?.name ?? id;
  const mine = new Set(v.systems.filter((s) => s.owner === v.me.id).map((s) => s.id));
  const rows: { key: string; kind: 'hot' | 'inbound' | 'soft'; text: string; cta: string; go: () => void }[] = [];
  for (const s of v.systems) if (mine.has(s.id) && (s.engaged || s.blockadedBy)) rows.push({ key: `hot-${s.id}`, kind: 'hot', text: (s.engaged ? t('alertBattle') : t('alertBlockade')).replace('{s}', s.name), cta: t('enter'), go: () => { systemMode.value = s.id; } });
  const now = v.time + ((Date.now() - hudReceivedAt) / 1000) * v.timeScale;
  for (const f of v.fleets) {
    if (f.owner === v.me.id || !f.destination || !mine.has(f.destination) || f.combat === 0 || f.at !== null) continue;
    if (v.colonies.find((c) => c.id === f.owner)?.ally) continue;
    const sys = v.systems.find((x) => x.id === f.destination);
    rows.push({ key: `in-${f.id}`, kind: 'inbound', text: t('alertInbound').replace('{n}', String(f.combat)).replace('{a}', name(f.owner)).replace('{s}', sys?.name ?? f.destination).replace('{t}', etaText(f.arriveAt - now)), cta: t('enter'), go: () => { systemMode.value = f.destination; } });
  }
  for (const b of v.barters) if (b.to === v.me.id && !b.accepted) rows.push({ key: `offer-${b.id}`, kind: 'soft', text: t('alertOffer').replace('{a}', name(b.from)), cta: t('review'), go: () => { tab.value = 'market'; } });
  for (const p of v.proposals) if (p.to === v.me.id) rows.push({ key: `prop-${p.from}-${p.kind}`, kind: 'soft', text: t('alertProposal').replace('{a}', name(p.from)).replace('{k}', t(p.kind as 'nap')), cta: t('review'), go: () => { tab.value = 'diplomacy'; } });
  for (const i of v.invites) rows.push({ key: `inv-${i.alliance}`, kind: 'soft', text: t('alertInvite').replace('{a}', i.name), cta: t('review'), go: () => { tab.value = 'diplomacy'; } });
  if (!rows.length) return null;
  const shown = rows.slice(0, 3);
  return (
    <div class="alerts">
      {shown.map((r) => (
        <button key={r.key} class={`alert ${r.kind}`} onClick={r.go}>
          <i /> <span>{r.text}</span> <b>{r.cta} ›</b>
        </button>
      ))}
      {rows.length > shown.length && <small class="muted">{t('alertsMore').replace('{n}', String(rows.length - shown.length))}</small>}
    </div>
  );
}

function Hud({ v, map }: { v: PlayerView; map: { current: GalaxyMap | null } }) {
  const [, force] = useState(0);
  const postV = useSig(post);
  const souvV = useSig(souvenirNow); const momV = useSig(momentNow); const captV = useSig(captureMode);
  const briefV = useSig(briefing), xpOpenV = useSig(expeditionOpen);
  const souvInline = postV === 'bridge' && !!souvV && !momV && !captV && !seanceV.value && !briefV && !xpOpenV;
  const [legend, setLegend] = useState(false);
  useEffect(() => { const i = setInterval(() => force((x) => x + 1), 1000); return () => clearInterval(i); }, []);
  const secondsLeft = Math.max(0, v.nextDrawAt - v.time - ((Date.now() - hudReceivedAt) / 1000) * v.timeScale);
  const frac = 1 - secondsLeft / 3600;
  const net = netStatus(v.me, v.time + ((Date.now() - hudReceivedAt) / 1000) * v.timeScale, v.timeScale);
  const ev = v.draw?.event.kind ?? 'none';
  const evLabel = ev === 'eruption' ? t('drawEventEruption') : ev === 'storm' ? t('drawEventStorm') : ev === 'echo' ? t('drawEventEcho') : t('drawEventNone');
  return (
    <div class="hud">
      <div class="chips">
        {RES.map((r) => (
          <span class={`chip r-${r}`} key={r} title={`${t(r)} — ${t(`${r}Desc`)}`}>
            <Icon name={r} /> <b>{fmt(v.me.stock[r])}</b><small>+{fmt(v.me.lastProduced[r])}</small><em>{t(r)}</em>
          </span>
        ))}
        <span class="chip r-credits" title={`${t('credits')} — ${t('creditsDesc')}`}><Icon name="credits" /> <b>{fmt(v.me.credits)}</b><em>{t('credits')}</em></span>
        <span class="chip r-influence" title={`${t('influence')} — ${t('influenceDesc')}`}><Icon name="influence" /> <b>{fmt(v.me.influence)}</b><em>{t('influence')}</em></span>
        <button class="chip help" onClick={() => setLegend(!legend)} title={t('help')}><Icon name="help" /></button>
      </div>
      <div class="status">
        <span class="draw" title={t('nextDraw')}>
          <svg width="26" height="26" viewBox="0 0 26 26"><circle cx="13" cy="13" r="10" fill="none" stroke="#2a3760" stroke-width="3" /><circle cx="13" cy="13" r="10" fill="none" stroke="#7dd3fc" stroke-width="3" stroke-dasharray={`${Math.max(0, frac) * 62.8} 62.8`} transform="rotate(-90 13 13)" /></svg>
          <span>{t('nextDraw')} <b>{hms(secondsLeft)}</b></span>
        </span>
        {v.me.objective && (
          <span class={`objective ${v.me.objective.met ? 'met' : ''}`} title={t('objective')}>
            {t('objective')} · <b>{objectiveText(v.me.objective)}</b>
            {v.me.objective.kind === 'connect' && <small> ({Math.min(v.me.objective.have, v.me.objective.count)}/{v.me.objective.count})</small>}
            {v.me.objective.met && <b class="ok"> ✓ {t('objMet')}</b>}
          </span>
        )}
        {v.draw && <span title={t('bands')}>{t('bands')} <b>{v.draw.bands.join(' · ')}</b> · <b class={`ev-${ev}`}>{evLabel}</b></span>}
        <span class="score" title={net.gain ? `${t('score')} ${v.me.score.toFixed(1)} · ${net.gain} ${t('atNextDraw')}` : undefined}>{t('score')} <b>{v.me.score.toFixed(1)}</b>{net.gain && <> · <b class="gain">{net.gain}</b><span class="long"> {t('atNextDraw')}</span></>}</span>
        <span class="net"><b>{net.connected}</b> {net.connected === 1 ? t('connectedOne') : t('connected')}{net.building > 0 && <> · <b class="building">{net.building}</b> {t('netBuilding')}{net.readyIn !== null && <small> ({(net.firstOnly ? t('netFirstReadyIn') : net.building === 1 ? t('netReadyInOne') : t('netReadyIn')).replace('{t}', minutesText(net.readyIn))})</small>}</>}</span>
        {v.me.shielded && <span class="tag">{t('shielded')}</span>}
      </div>
      {legend && (
        <div class="legend" onClick={() => setLegend(false)}>
          <h3>{t('legend')}</h3>
          {RES.map((r) => <p key={r}><span class={`r-${r}`}><Icon name={r} /></span> <b>{t(r)}</b> — {t(`${r}Desc`)}</p>)}
          <p><span class="r-credits"><Icon name="credits" /></span> <b>{t('credits')}</b> — {t('creditsDesc')}</p>
          <p><span class="r-influence"><Icon name="influence" /></span> <b>{t('influence')}</b> — {t('influenceDesc')}</p>
          <p class="muted">{t('coach3')}</p>
          <p class="row"><a href="/gazette" target="_blank" rel="noopener">{t('gazette')}</a> · <button class="link" onClick={() => { setLegend(false); startCapture(); }}>{t('captureMode')}</button></p>
          <InstallButton compact />
        </div>
      )}
      <UpdateBanner />
      <Alerts v={v} />
      <ExpeditionBanner />
      <AnchorBanner v={v} />
      {/* #92: on the Bridge the memory takes its place above the counsel instead of covering its last line. */}
      {souvInline && <SouvenirCard v={v} inline />}
      {postV !== 'table' && (v.me.counsel.length > 0 ? <Counsel v={v} map={map} /> : <Coach v={v} />)}
    </div>
  );
}

/** A memory that comes back. #89: it comes back as a line (title + first sentence); the full text only when asked for. */
function SouvenirCard({ v, inline = false }: { v: PlayerView; inline?: boolean }) {
  const souv = useSig(souvenirNow);
  const [souvOpen, setSouvOpen] = useState(false);
  useEffect(() => { setSouvOpen(false); }, [souv?.id]);
  if (!souv) return null;
  return (
    <div class={`souvenir ${souvOpen ? 'open' : 'compact'} ${inline ? 'inline' : ''}`} role="status" aria-label={t('souvenirTitle')}>
      <small>{t('souvenirTitle')} · {t(v.me.persona as 'vane')}</small>
      <h3>{souv.title[lang.value]}</h3>
      {souvOpen ? <p>{souv.text[lang.value]}</p> : <p class="lede">{firstSentence(souv.text[lang.value])}</p>}
      <div class="actions">
        {souvOpen
          ? <><button class="primary" onClick={() => { setSouvOpen(false); closeSouvenir(souv.id); }}>{t('close')}</button><button onClick={() => { setSouvOpen(false); closeSouvenir(souv.id); if (post.value !== 'table') tab.value = 'general'; archivesOpen.value = true; }}>{t('souvenirArchives')}</button></>
          : <><button class="primary" onClick={() => setSouvOpen(true)}>{t('souvenirRead')}</button><button onClick={() => closeSouvenir(souv.id)}>{t('souvenirLater')}</button></>}
      </div>
    </div>
  );
}

/** The General's name without the title (« L'Abbé Solen » → « Solen »), for the narrow places. */
const personaShort = (persona: string): string => t(persona as 'vane').replace(/^(L'Abbé |Maréchale |Marshal |The Abbot )/, '');

/** Decision 0014, the Table: the General beside the map — who he is, his counsel, where to remember, what comes next,
 *  his voice. The map stays in view the whole time. */
function Dock({ v, map }: { v: PlayerView; map: { current: GalaxyMap | null } }) {
  const a = useSig(archivesData);
  useEffect(() => { if (!a) void refreshArchives(); }, []);
  const found = a?.found.length ?? 0, total = a?.arcs.reduce((n, x) => n + x.total, 0) ?? 0;
  return (
    <aside class="dock" aria-label={t('tabGeneral')}>
      <header class="gen">
        <span class={`sigil p-${v.me.persona}`} aria-hidden="true">{personaShort(v.me.persona).slice(0, 1)}</span>
        <div>
          <b>{t(v.me.persona as 'vane')}</b>
          <small>{t(`${v.me.persona}Desc` as 'vaneDesc')}</small>
          <Constellation persona={v.me.persona} />
          {total > 0 && <small class="mem">{t('dockMemory').replace('{f}', String(found)).replace('{t}', String(total))}{(a?.echoes ?? 0) > 0 ? ` · ${t('echoesHeld').replace('{n}', String(a?.echoes ?? 0))}` : ''}</small>}
        </div>
      </header>
      {v.me.counsel.length > 0 ? <Counsel v={v} map={map} /> : <Coach v={v} />}
      <GeneralPanel v={v} />
    </aside>
  );
}

/** Decision 0014: the places of memory — the systems this Colony knows that answer a hint of a Séance. */
function Places({ v, map }: { v: PlayerView; map: { current: GalaxyMap | null } }) {
  const a = useSig(archivesData);
  const hints = (a?.hints ?? []).filter((h) => h.places);
  if (!hints.length) return null;
  const L = lang.value;
  const name = (id: string): string => v.systems.find((s) => s.id === id)?.name ?? id;
  return (
    <section class="places">
      <h3>{t('placesTitle')}</h3>
      {hints.map((h) => (
        <div key={h.id} class="place">
          <p class="muted small">{t('hintToFind').replace('{h}', h.hint[L])}</p>
          {(h.places ?? []).length ? (
            <div class="sys">{(h.places ?? []).map((id) => {
              const mine = v.systems.find((s) => s.id === id)?.owner === v.me.id;
              return <button key={id} class={mine ? 'mine' : ''} onMouseEnter={() => map.current?.flash(id)} onClick={() => { selected.value = id; tab.value = 'system'; map.current?.centerOn(id); map.current?.flash(id); }}>{mine ? '◆ ' : ''}{name(id)}</button>;
            })}</div>
          ) : <p class="muted small">{t('placesNone')}</p>}
        </div>
      ))}
    </section>
  );
}

/** Decision 0014: the next Draws, simulated on a copy of the world with fictitious bands (a range, never the future). */
function Projection({ v }: { v: PlayerView }) {
  const [p, setP] = useState<ProjectionView | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // 12 to 17 s on the production world: said while it runs, and a failure is said too (QA 10.10: a lost first click left nothing).
  const run = async (): Promise<void> => { setBusy(true); setFailed(false); const r = await fetchProjection(); if (r) setP(r); else setFailed(true); setBusy(false); };
  const fmtR = (r: [number, number]): string => (Math.round(r[0]) === Math.round(r[1]) ? fmt(r[0]) : `${fmt(r[0])}–${fmt(r[1])}`);
  return (
    <section class="projection">
      <h3>{t('projTitle')}</h3>
      {!p && <p class="muted small">{t('projHelp')}</p>}
      {p && p.draws.length > 0 && (
        <table>
          <thead><tr><th>{t('projDraw')}</th>{RES.map((r) => <th key={r} class={`r-${r}`} title={t(r)}><Icon name={r} size={13} /></th>)}<th>{t('score')}</th><th title={t('projDarkHelp')}>{t('projDark')}</th></tr></thead>
          <tbody>
            <tr class="now"><td>{t('projNow')}</td>{RES.map((r) => <td key={r}>{fmt(v.me.stock[r])}</td>)}<td>{v.me.score.toFixed(1)}</td><td>–</td></tr>
            {p.draws.map((d, i) => (
              <tr key={d.index}><td>+{i + 1}</td>{RES.map((r) => <td key={r}>{fmtR(d.stock[r] ?? [0, 0])}</td>)}<td>{fmtR(d.score)}</td><td class={d.dark > 0 ? 'bad' : ''}>{d.dark}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {p && <p class="muted small">{t('projNote')}</p>}
      {busy && <p class="small busy" role="status">{t('projBusy')}</p>}
      {failed && !busy && <p class="error small" role="alert">{t('projFailed')}</p>}
      <button onClick={() => void run()} disabled={busy}>{busy ? '…' : p ? t('projAgain') : t('projRun')}</button>
    </section>
  );
}

/** Decision 0012: the memory on screen, the ones behind it, and the ones already read on this device. */
type SouvenirCard = { id: string; title: { fr: string; en: string }; text: { fr: string; en: string } };
const souvenirNow = signal<SouvenirCard | null>(null);
const souvenirQueue: SouvenirCard[] = [];
const archivesOpen = signal(false);
/** The Archives, shared by the General's tab and the system sheet (the Echoes in hand), and the Séance on screen. */
const archivesData = signal<ArchivesView | null>(null);
let archivesMarks = -1;
async function refreshArchives(): Promise<void> { const a = await fetchArchives(); if (a) { archivesData.value = a; setCounselTaken(a.counselTaken); } }
const seanceV = signal<SeanceView | null>(null);
let seanceChecked = false;

/** Decision 0013: the Séance. The world opens the fragments; the General tells them; it ends on the memory or the hint. */
function SeanceOverlay({ v }: { v: PlayerView }) {
  const sv = useSig(seanceV);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // the General's lines already engraved (a new one is cut word by word; the old ones stay still)
  const seen = useRef(-1);
  // the light goes down around the panel, wherever the layout puts it (dock on the left, bottom on a narrow screen)
  const veil = useRef<HTMLDivElement>(null), panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const p = panel.current?.getBoundingClientRect(), v = veil.current;
    if (p && v) { v.style.setProperty('--vx', `${p.left + p.width / 2}px`); v.style.setProperty('--vy', `${p.top + p.height / 2}px`); v.style.setProperty('--vr', `${Math.max(p.width, p.height) * 0.75}px`); }
  });
  const opened = useRef<string | null>(null);
  useEffect(() => { seen.current = sv ? sv.history.length : -1; });
  useEffect(() => { if (!seanceChecked) { seanceChecked = true; void fetchSeance().then((x) => { if (x && !x.done) seanceV.value = x; }); } }, []);
  if (!sv) { opened.current = null; return null; }
  const firstFrame = opened.current !== sv.system;
  if (firstFrame) opened.current = sv.system;
  const fresh = (k: number): boolean => firstFrame ? k === sv.history.length - 1 : k >= seen.current;
  const recall = recallLine(archivesData.value?.expeditions ?? [], lang.value, sv.system);
  const send = async (e: Event) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    const said = text.trim();
    setBusy(true); setText('');
    seanceV.value = { ...sv, history: [...sv.history, { who: 'me', text: said }] };
    const r = await sayInSeance(said);
    setBusy(false);
    if (r.seance) seanceV.value = r.seance;
  };
  const close = () => { void apiCloseSeance(); seanceV.value = null; void refreshArchives(); };
  return (
    <>
    <div class="seance-veil" aria-hidden="true" ref={veil} />
    <div class="seance" role="dialog" aria-label={t('seanceTitle')} ref={panel}>
      <header>
        <small>{t('seanceTitle')} · {t(v.me.persona as 'vane')} · {sv.systemName}</small>
        <span class="frags" aria-label={t('seanceFragments')}>{[0, 1, 2].map((k) => <i key={k} class={k < sv.open ? 'on' : ''} />)}</span>
      </header>
      <div class="thread">
        {recall && <RecallFlash label={t('generalRemembers')} text={recall} />}
        {sv.history.map((h, k) => <p key={k} class={h.who === 'me' ? 'me' : 'general'}>{h.who === 'general' ? <Engraved text={h.text} play={fresh(k)} delay={recall && firstFrame ? 1900 : 0} /> : h.text}</p>)}
        {busy && <p class="general muted">…</p>}
      </div>
      {sv.done ? (
        <div class="end">
          {sv.outcome === 'remembered' ? <RecallFlash label={t('generalRemembers')} text={t('seanceRemembered')} /> : <p class="muted small">{t('seanceHint')}</p>}
          <button class="primary" onClick={close}>{t('close')}</button>
        </div>
      ) : (
        <form onSubmit={(e) => void send(e)}>
          <textarea value={text} onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} placeholder={t('seancePlaceholder')} maxLength={800} rows={2} disabled={busy} />
          <div class="actions"><button class="primary" disabled={busy || !text.trim()}>{t('send')}</button><button type="button" onClick={close}>{t('seanceLeave')}</button></div>
        </form>
      )}
    </div>
    </>
  );
}
function souvenirShown(): Set<string> { try { return new Set(JSON.parse(localStorage.getItem('aurane.souvenirs') ?? '[]') as string[]); } catch { return new Set(); } }
// The line a memory comes back with: whole sentences, at least 80 characters (« Ce relais tient. Bien. » said nothing, QA 10.10).
const firstSentence = (x: string): string => { const m = /^(.{80,240}?[.!?…])(\s|$)/.exec(x); return m ? m[1]! : x.slice(0, 200); };
function closeSouvenir(id: string): void {
  try { localStorage.setItem('aurane.souvenirs', JSON.stringify([...souvenirShown(), id].slice(-400))); } catch { /* ignore */ }
  souvenirNow.value = souvenirQueue.shift() ?? null;
}

/** The moment on screen, the queue behind it, and the last one (the capture mode replays it). */
const momentNow = signal<(Moment & { plan: MomentPlan; title: string; gazette?: string }) | null>(null);
/** The Gazette's title of the day, fetched at the Draw (issue #48, idea 5); null when no issue is out yet. */
let gazetteTitle: { lang: string; at: number; title: string | null } | null = null;
const captureMode = signal(false);
let momentsSeen: Set<string> | null = null;
let momentMap: GalaxyMap | null = null;
let lastMoment: Moment | null = null;
const momentQueue: Moment[] = [];
const momentTimers: ReturnType<typeof setTimeout>[] = [];

function playMoment(m: Moment, replay = false): void {
  const v = view.value;
  if (!v) return;
  const reduced = prefersReducedMotion();
  const plan = m.kind === 'draw' ? drawPlan(reduced) : momentPlan(replay ? 0 : playedCounts()[m.kind], reduced);
  if (!replay) countPlayed(m.kind);
  lastMoment = m;
  const name = v.systems.find((x) => x.id === m.system)?.name ?? m.system;
  const map = momentMap;
  if (m.kind === 'draw') {
    momentNow.value = { ...m, plan, title: t('momentDraw').replace('{n}', String((m.index ?? 0) + 1)) };
    // Band by band, my systems in it flash (nothing moves with reduced motion); the Gazette's title if an issue is out.
    if (!reduced) (m.lit ?? []).forEach((ids, i) => momentTimers.push(setTimeout(() => { for (const id of ids) map?.flash(id); }, 500 + i * 700)));
    void gazetteOfTheDay(lang.value, m.at).then((title) => { if (title && momentNow.value?.key === m.key) momentNow.value = { ...momentNow.value, gazette: title }; });
    momentTimers.push(setTimeout(endMoment, plan.titleMs));
    return;
  }
  momentNow.value = { ...m, plan, title: t(m.kind === 'relay' ? 'momentRelay' : m.kind === 'welcome' ? 'momentWelcome' : m.kind === 'wormhole' ? 'momentWormhole' : 'momentContact').replace('{s}', name) };
  // Reduced motion: no flight, no run, no rings; the title fades in and out.
  if (plan.camera) map?.flyTo(m.system, plan.cameraMs);
  const land = (): void => { if (plan.camera) map?.flash(m.system); };
  if (plan.run && m.path.length > 1) momentTimers.push(setTimeout(() => map?.signalRun(m.path, plan.runMs, land), plan.cameraMs));
  else momentTimers.push(setTimeout(land, plan.cameraMs));
  momentTimers.push(setTimeout(endMoment, plan.cameraMs + plan.titleMs));
}

/** The Gazette's title of the day, at most one request per Draw and language. */
async function gazetteOfTheDay(l: string, at: number): Promise<string | null> {
  if (gazetteTitle && gazetteTitle.lang === l && gazetteTitle.at === at) return gazetteTitle.title;
  let title: string | null = null;
  try { const r = await fetch(`/api/public/gazette?lang=${l}`); if (r.ok) title = ((await r.json()) as { title?: string }).title ?? null; } catch { /* no Gazette: the reveal goes on without it */ }
  gazetteTitle = { lang: l, at, title };
  return title;
}

function endMoment(): void {
  while (momentTimers.length) clearTimeout(momentTimers.pop());
  momentNow.value = null;
  nextMoment();
}

function nextMoment(): void {
  const next = momentQueue.shift();
  if (next) playMoment(next);
}

/** A tap passes: the camera lands, the pulse arrives, the next moment (if any) follows. */
function skipMoment(): void {
  if (captureMode.value) { exitCapture(); return; }
  momentMap?.finishMoment();
  endMoment();
}

/** Capture mode (issue #48): every panel hidden, the last moment replayed in full, for a 5-second clip. A tap or Esc leaves. */
function startCapture(): void {
  captureMode.value = true;
  window.addEventListener('keydown', onCaptureKey);
  if (lastMoment) { momentMap?.finishMoment(); while (momentTimers.length) clearTimeout(momentTimers.pop()); setTimeout(() => playMoment(lastMoment!, true), 300); }
}

function exitCapture(): void {
  captureMode.value = false;
  window.removeEventListener('keydown', onCaptureKey);
}

const onCaptureKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') exitCapture(); };

/** What covers the map on screen: the HUD (chips, status, Counsel) on top, the panel on the right (desktop) or at the
 *  bottom (phone). The first frame of the map lands in what is left (issue #37). */
function mapInsets(host: HTMLElement | null): { top: number; right: number; bottom: number; left: number } {
  const box = host?.getBoundingClientRect();
  // Capture mode hides every panel: the whole canvas is the frame.
  if (!box || captureMode.value) return { top: 0, right: 0, bottom: 0, left: 0 };
  const hud = document.querySelector('.hud')?.getBoundingClientRect();
  const card = document.querySelector('.hud .counsel, .hud .coach')?.getBoundingClientRect();
  const panel = document.querySelector('.panel')?.getBoundingClientRect();
  const dock = document.querySelector('.dock')?.getBoundingClientRect();
  const side = !!panel && panel.width < box.width * 0.6;
  const right = side && panel ? Math.max(0, box.right - panel.left) : 0;
  const bottom = !side && panel ? Math.max(0, box.bottom - panel.top) : 0;
  // A tall HUD (the Counsel on a phone) would leave a sliver: keep at least 40 % of the height for the map.
  const cap = (y: number): number => Math.min(Math.max(0, y - box.top), box.height * 0.6);
  type I = { top: number; right: number; bottom: number; left: number };
  const side2 = (i: I): number => Math.min(box.width - i.left - i.right, box.height - i.top - i.bottom);
  const below: I = { top: hud ? cap(hud.bottom) : 0, right, bottom, left: dock ? Math.max(0, dock.right - box.left) : 0 };
  // The Counsel sits top left: the map is either under it or beside it (desktop), whichever leaves the larger square
  // (a network spreads about as wide as tall, so the short side sets the zoom).
  const beside: I | null = card ? { top: cap(card.top), right, bottom, left: Math.max(0, card.right - box.left) } : null;
  const best = beside && side2(beside) > side2(below) ? beside : below;
  if (side2(best) >= 160) return best;
  // No room left (the Counsel and an open panel on a phone): aim under the Counsel, where the map shows once the panel
  // folds (the HUD is capped at 60 % of the height, so that band always exists).
  return { ...below, bottom: 0 };
}

/** The Draw Counsel (0009): the Partner's cards. In the General's voice when the LLM layer wrote them (one fetch
 *  per Draw), else the simulation's cards with fixed lines. "Show me" opens the screen, "Do it" runs the ready
 *  command, "Not now" hides the card for this Draw; the General remembers both answers. */
const skippedCounsel = signal<Set<string>>(new Set());
const voiceCounsel = signal<CounselView | null>(null);
let counselFetching = false;

type UiCard = { id: string; title: string; line: string; command: Command | null; hasCommand: boolean; urgency: 0 | 1 | 2; voice: boolean; go: () => void; run: () => Promise<boolean> };

type TKey = Parameters<typeof t>[0];
const T = (k: string): string => String(t(k as TKey));
const SEP = ' › ';

/** The voice layer's `show` (packages/general) as the simulation's target, so one function shows both; the
 *  simulation's own target rides along as `raw` when the card came from its Counsel. */
function fromVoiceShow(sh: CounselCard['show'], raw?: unknown): ShowTarget | null {
  const r = raw as { kind?: unknown } | undefined;
  if (r && (r.kind === 'star' || r.kind === 'link' || r.kind === 'plateau' || r.kind === 'tab')) return r as ShowTarget;
  if (!sh) return null;
  if (sh.screen === 'system' && sh.system) {
    if (sh.slot === 'link') return { kind: 'link', from: sh.system, to: null };
    if (sh.slot || sh.poi) { const o = Number(sh.slot); return { kind: 'plateau', system: sh.system, orbit: o === 1 || o === 2 || o === 3 ? o : null }; }
    return { kind: 'star', system: sh.system };
  }
  if (sh.screen === 'journal') return { kind: 'tab', tab: 'log' };
  if (sh.screen === 'colony' || sh.screen === 'market' || sh.screen === 'general') return { kind: 'tab', tab: sh.screen };
  return null; // the galaxy
}

/** Where a card without a ready command takes the player, with the path said out loud. */
function showTarget(show: ShowTarget | null, v: PlayerView, map: GalaxyMap | null): void {
  const sys = (id: string): string => v.systems.find((x) => x.id === id)?.name ?? id;
  const here = (key: string | null, path: string[]): void => pointAt(key, t('hereIsWhere').replace('{path}', path.join(SEP)) + (key ? ` ${t('tapBlink')}` : ''));
  if (!show) { systemMode.value = null; tab.value = 'colony'; }
  else if (show.kind === 'star') { systemMode.value = null; selected.value = show.system; tab.value = 'system'; map?.centerOn(show.system); map?.flash(show.system); here(`enter:${show.system}`, [sys(show.system), t('tabSystem'), t('enterSystem')]); }
  else if (show.kind === 'link') { systemMode.value = null; selected.value = show.from; tab.value = 'system'; linkFrom.value = show.from; map?.centerOn(show.from); map?.flash(show.from); here(show.to ? `link:${show.to}` : null, [sys(show.from), t('tabSystem'), t('linkMode'), ...(show.to ? [sys(show.to)] : [])]); }
  else if (show.kind === 'plateau') { pendingDemo.value = { system: show.system, dock: 'plateau', orbit: show.orbit }; systemMode.value = show.system; here(null, [sys(show.system), t('enterSystem'), t('plateau'), ...(show.orbit ? [T(`orbit${show.orbit}`)] : [])]); }
  else { systemMode.value = null; tab.value = show.tab; here(show.tab === 'general' ? 'say' : show.tab === 'log' ? 'recap' : null, [T(`tab${show.tab[0]!.toUpperCase()}${show.tab.slice(1)}`)]); }
  openPanel.value++;
}

/** The path to the button that sends this command, in the words of the screens. */
function pathOf(cmd: Command, v: PlayerView): string {
  const sys = (id: string): string => v.systems.find((x) => x.id === id)?.name ?? id;
  const who = (id: string): string => v.colonies.find((c) => c.id === id)?.name ?? id;
  switch (cmd.type) {
    case 'build_relay': return [sys(cmd.a), t('tabSystem'), t('linkMode'), sys(cmd.b)].join(SEP);
    case 'build': return [sys(cmd.system), t('enterSystem'), t('plateau'), T(`orbit${BUILDING_ORBIT[cmd.building]}`), T(cmd.building)].join(SEP);
    case 'train': return [sys(cmd.system), t('enterSystem'), t('plateau'), t('train'), T(cmd.unit)].join(SEP);
    case 'fleet_order': return [sys(cmd.target.split(':')[0]!), t('enterSystem'), t('tabFleets'), T(cmd.order)].join(SEP);
    case 'market_order': return [t('tabMarket'), `${T(cmd.side)} ${T(cmd.resource)}`, t('place')].join(SEP);
    case 'treaty': return [t('tabDiplomacy'), who(cmd.with), T(cmd.kind)].join(SEP);
    case 'decree': return [t('tabColony'), t('decrees'), decreeLabel(cmd.kind)].join(SEP);
    default: return '';
  }
}

/** Take the player to the button a command would press and make it blink ("Show me"), or leave the path after the
 *  General pressed it ("Do it", done = true). */
function demonstrate(cmd: Command, v: PlayerView, map: GalaxyMap | null, done = false): void {
  const key = teachKey(cmd);
  const path = pathOf(cmd, v);
  if (!done) {
    switch (cmd.type) {
      case 'build_relay': systemMode.value = null; selected.value = cmd.a; tab.value = 'system'; linkFrom.value = cmd.a; map?.centerOn(cmd.a); map?.flash(cmd.a); openPanel.value++; break;
      case 'build': pendingDemo.value = { system: cmd.system, dock: 'plateau', orbit: BUILDING_ORBIT[cmd.building] }; systemMode.value = cmd.system; break;
      case 'train': pendingDemo.value = { system: cmd.system, dock: 'plateau', orbit: null }; systemMode.value = cmd.system; break;
      case 'fleet_order': { const at = cmd.target.split(':')[0]!; pendingDemo.value = { system: at, dock: 'fleets', orbit: null }; systemMode.value = at; break; }
      case 'market_order': systemMode.value = null; marketPrefill.value = { region: cmd.region, resource: cmd.resource, side: cmd.side, qty: cmd.qty, price: cmd.price }; tab.value = 'market'; openPanel.value++; break;
      case 'treaty': systemMode.value = null; tab.value = 'diplomacy'; openPanel.value++; break;
      case 'decree': systemMode.value = null; tab.value = 'colony'; openPanel.value++; break;
      default: break;
    }
  }
  if (!path) return;
  if (done) pointAt(null, t('howTo').replace('{path}', path), true, 8000);
  else pointAt(key, t('hereIsWhere').replace('{path}', path) + (key ? ` ${t('tapBlink')}` : ''));
}

/** The star a command acts on, to show where the General just did something. */
function commandTarget(cmd: Command | null): string | null {
  if (!cmd) return null;
  switch (cmd.type) {
    case 'build_relay': return cmd.b;
    case 'build': case 'train': return cmd.system;
    case 'fleet_order': return cmd.target.split(':')[0]!;
    default: return null;
  }
}

const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Cards the player set aside with "Not now" (hidden for the Draw, no "done" mark), and the look-only cards already
 *  answered as seen (sent once). */
const dismissedCounsel = signal<Set<string>>(new Set());
const lookedAt = new Set<string>();
/** Cards that came in as others were done (the next tier's advice): they arrive visibly, not in place of the old. */
const freshCounsel = signal<Set<string>>(new Set());
const FRESH_MS = 4000;
/** What this screen saw reached this Draw beyond the journal (a doctrine written, a building placed by hand), id →
 *  title: joins the world's record in the « ✓ Fait ce Tirage » line, cleared by the next Draw. */
const doneSession = signal<{ draw: number; cards: Map<string, string> }>({ draw: -1, cards: new Map() });
view.subscribe((v) => { if (!v) doneSession.value = { draw: -1, cards: new Map() }; }); // logged out: another Colony
let lastShown: ShownCard[] = [];
let lastShownDraw = -1;
/** What the Counsel wants from the voice layer right now; a fetch that answered for another tier is retried once. */
let counselWanted = { draw: -1, tier: -1 };

/** Cards whose goal was just reached join the « ✓ Fait ce Tirage » line (it replaces the fading "✓ Done" mark of
 *  #33, issue #35); the next tier's cards that came in with them fade in. */
function markDone(cards: { id: string; title: string }[], fresh: string[], draw: number): void {
  if (!cards.length) return;
  const kept = doneSession.value.draw === draw ? doneSession.value.cards : new Map<string, string>();
  doneSession.value = { draw, cards: new Map([...kept, ...cards.map((c) => [c.id, c.title] as [string, string])]) };
  if (!fresh.length) return;
  freshCounsel.value = new Set([...freshCounsel.value, ...fresh]);
  setTimeout(() => { const f = new Set(freshCounsel.value); for (const id of fresh) f.delete(id); freshCounsel.value = f; }, FRESH_MS);
}

/** A countdown card's minutes as of now, not as of the last view (the view only comes when something changes). */
function liveMinutes<C extends { params: Record<string, unknown> }>(c: C, v: PlayerView): C {
  const readyAt = c.params.readyAt;
  if (typeof readyAt !== 'number') return c;
  const now = v.time + ((Date.now() - hudReceivedAt) / 1000) * v.timeScale;
  return { ...c, params: { ...c.params, min: Math.max(1, Math.ceil((readyAt - now) / 60 / Math.max(1, v.timeScale))) } };
}

/** A command that spends resources, Credits or Influence (a relay, a building, ships, a trade, a decree, a pact). */
// Sending a fleet (the scout) counts too (#77: « Link » and « Scout » both offered « Do it » after the first relay).
const spends = (cmd: Command | null): boolean => !!cmd && ['build_relay', 'build', 'train', 'market_order', 'decree', 'treaty', 'agent_mission', 'light_beacon', 'fleet_order'].includes(cmd.type);

/** « Relay being built: ready in N min », counted from now and redrawn every 10 s (#79: it stayed at 10 min while the
 *  status bar said 8; the view only comes when something changes). */
function LiveEta({ readyAt, v }: { readyAt: number; v: PlayerView }) {
  const [, tick] = useState(0);
  useEffect(() => { const i = setInterval(() => tick((x) => x + 1), 10000); return () => clearInterval(i); }, []);
  const now = v.time + ((Date.now() - hudReceivedAt) / 1000) * v.timeScale;
  return <p class="small relay-eta">{t('relayBuilding').replace('{t}', minutesText((readyAt - now) / Math.max(1, v.timeScale)))}</p>;
}

function Counsel({ v, map }: { v: PlayerView; map: { current: GalaxyMap | null } }) {
  const [allCounsel, setAllCounsel] = useState(false);
  useSig(post);
  const [, tickCounsel] = useState(0);
  const counting = v.me.counsel.some((c) => typeof (c.params as Record<string, unknown>).readyAt === 'number');
  useEffect(() => { if (!counting) return; const i = setInterval(() => tickCounsel((x) => x + 1), 10000); return () => clearInterval(i); }, [counting]);
  const skipped = useSig(skippedCounsel);
  const dismissed = useSig(dismissedCounsel);
  const fresh = useSig(freshCounsel);
  const voice = useSig(voiceCounsel);
  const session = useSig(doneSession);
  const sel = useSig(selected);
  const sysMode = useSig(systemMode);
  const curTab = useSig(tab);
  const nextDraw = Math.floor(v.time / 3600) + 1;
  const tier = v.me.onboarding?.tier ?? 6;
  // One fetch per Draw and per onboarding tier: the voice cards are cached server-side until the Draw, rewritten
  // when a tier opens, and re-checked against the world on every read.
  counselWanted = { draw: nextDraw, tier };
  // A tier that opens while a fetch is in flight (a quick "Do it" on the first relay) must not be lost: the answer
  // for the old tier is retried once for the tier the colony is at now.
  const refetch = (retry = true): void => {
    if (counselFetching) return;
    counselFetching = true;
    void fetchCounsel(lang.value).then((c) => { if (c) voiceCounsel.value = c; }).finally(() => {
      counselFetching = false;
      const c = voiceCounsel.value;
      if (retry && c && (c.drawIndex !== counselWanted.draw || (c.tier !== undefined && c.tier !== counselWanted.tier))) refetch(false);
    });
  };
  useEffect(() => { if (!voice || voice.drawIndex !== nextDraw || (voice.tier !== undefined && voice.tier !== tier)) refetch(); }, [nextDraw, tier]);
  // A card whose goal is a place to look is done once the player is there, however they got there.
  useEffect(() => {
    for (const c of v.me.counsel) {
      if (c.command || lookedAt.has(c.id)) continue;
      const there = (c.kind === 'touch_star' && sel === v.me.capital) || (c.kind === 'enter_system' && sysMode === v.me.capital) || (c.kind === 'read_recap' && curTab === 'log');
      if (there) { lookedAt.add(c.id); void act({ type: 'counsel_answer', id: c.id, taken: true }); }
    }
  }, [sel, sysMode, curTab, v.me.counsel.map((c) => c.id).join()]);
  const answer = (id: string, taken: boolean, viaVoice: boolean) => {
    if (!taken) dismissedCounsel.value = new Set([...dismissedCounsel.value, id]);
    skippedCounsel.value = new Set([...skippedCounsel.value, id]);
    if (viaVoice) void answerCounsel(id, taken); else void act({ type: 'counsel_answer', id, taken });
  };
  // "Do it" teaches: go where the button is, let the player see it, act, then show what appeared and leave the path.
  const doIt = async (cmd: Command, send: () => Promise<{ ok: boolean; reply: string | null; gone?: boolean }>): Promise<boolean> => {
    demonstrate(cmd, v, map.current);
    await wait(cmd.type === 'build_relay' ? 700 : 1100);
    if (systemMode.value) sceneFlashReq.value++;
    const r = await send();
    // The demonstration opened the link mode to show the button; the General pressed it (or could not): close it,
    // or "Tap a green star to link it" stays on screen as if something were still expected.
    if (cmd.type === 'build_relay' && linkFrom.value === cmd.a) linkFrom.value = null;
    // Already done (by hand, meanwhile) or no longer on the table: the card leaves, nothing to press.
    if (r.gone) { stopTeaching(); refetch(); return true; }
    if (!r.ok) { stopTeaching(); return false; }
    demonstrate(cmd, v, map.current, true);
    const target = commandTarget(cmd);
    if (target && !systemMode.value) { selected.value = target; map.current?.centerOn(target); map.current?.flash(target); foldPanel.value++; }
    const reply = r.reply ?? t('counselDone');
    toast.value = { text: reply, kind: 'ok' }; setTimeout(() => { if (toast.value?.text === reply) toast.value = null; }, 4000);
    return true;
  };
  // "Show me": the button itself when the card carries a command, else the screen the card is about.
  const showMe = (cmd: Command | null, show: ShowTarget | null): void => { if (cmd) demonstrate(cmd, v, map.current); else showTarget(show, v, map.current); };
  // The voice cards are written once per Draw. The simulation's options move with the game (a relay opens tier 1
  // within the first minute), so a voice card stays while its option is still on the table, and a live option the
  // voice has not phrased shows with the fixed line: the General's words when it has them, never stale advice.
  // A card that counts down (« ready in 9 min », calibration playtest 29.09) always shows the live line: the voice
  // writes once per Draw and its figure would freeze.
  const voiceCards = (voice && voice.drawIndex === nextDraw ? voice.cards : []).filter((c) => !c.id.startsWith('reach:'));
  const liveIds = new Set(v.me.counsel.map((c) => c.id));
  const spoken = new Set(voiceCards.map((c) => c.id));
  const firstWords = voiceCards.length > 0 && voiceCards.every((c) => c.id.startsWith('first-')) && tier <= 0;
  const fromVoice: UiCard[] = voiceCards.filter((c) => firstWords || liveIds.has(c.id)).map((c) => ({ id: c.id, title: c.title, line: c.line, command: c.command, hasCommand: !!c.command, urgency: 1 as const, voice: true, go: () => showMe(c.command, fromVoiceShow(c.show, c.raw)),
    run: () => doIt(c.command!, () => answerCounsel(c.id, true)) }));
  const fromSim: UiCard[] = v.me.counsel.filter((c) => !spoken.has(c.id)).map((c) => ({ id: c.id, title: counselTitle(c, lang.value), line: counselLine(liveMinutes(c, v), lang.value), command: c.command, hasCommand: !!c.command, urgency: c.urgency, voice: false, go: () => showMe(c.command, c.show),
    run: () => doIt(c.command!, async () => ({ ok: await act(c.command!), reply: null })) }));
  const order = new Map(v.me.counsel.map((c, i) => [c.id, i]));
  const cards = [...fromVoice, ...fromSim].sort((a, b) => (order.get(a.id) ?? 99) - (order.get(b.id) ?? 99));
  const shown = cards.filter((c) => !skipped.has(c.id));
  // One « Do it » that spends at a time (decision Nick, 30.09, after the calibration playtest: two relays and a scout
  // launched in a row without a look at the map). The next card offers it once this one is done or set aside.
  const firstDoIt = shown.find((c) => c.hasCommand && spends(c.command))?.id;
  // The goals reached this Draw stay said at the top until the Draw (issue #35): the new tier's cards take the same
  // places, and a mark that fades leaves the player thinking nothing happened.
  const doneNow = doneThisDraw(v, session.draw === nextDraw ? session.cards : new Map(), lang.value);
  // A card that was on screen and left because its goal is reached (taken, looked at, or done by hand) joins that
  // line; a card only pushed out of the top three by the next tier's advice leaves quietly (issue #33).
  useEffect(() => {
    const ids = new Set(shown.map((c) => c.id));
    // A new Draw renews the whole Counsel: nothing was "done", the hour turned.
    if (lastShownDraw === nextDraw) {
      const answered = new Set([...[...skipped].filter((id) => !dismissed.has(id)), ...lookedAt]);
      const gone = doneCards(lastShown, ids, v, answered, new Set([...dismissed, ...doneNow.map((d) => d.id)]));
      const before = new Set(lastShown.map((c) => c.id));
      markDone(gone, shown.filter((c) => !before.has(c.id)).map((c) => c.id), nextDraw);
    }
    lastShown = shown.map((c) => ({ id: c.id, title: c.title, command: c.command }));
    lastShownDraw = nextDraw;
  });
  const unfolded = useSig(counselOpen);
  const narrowV = useSig(narrow);
  const brief = useSig(briefing);
  const xpOpen = useSig(expeditionOpen);
  // An URGENT or NEW card (Nick, 12.10) unfolds the Counsel's line by itself ONCE, when it arrives on screen (the
  // panel folds: never both open); after that the player's fold holds. A card read unfolded counts as seen too.
  const lead = shown[0];
  const leadLoud = !!lead && (lead.urgency === 2 || fresh.has(lead.id));
  useEffect(() => {
    if (!narrowV || !lead || sysMode || brief || xpOpen) return;
    if (seenCounsel(v.me.id).has(lead.id)) return;
    if (unfolded ?? tier <= 0) { markCounselSeen(v.me.id, lead.id); return; }
    if (!leadLoud) return;
    markCounselSeen(v.me.id, lead.id);
    counselOpen.value = true;
    foldPanel.value++;
  }, [lead?.id, leadLoud, unfolded, sysMode, brief, xpOpen, narrowV]);
  if (shown.length === 0 && doneNow.length === 0) return null;
  const tally = [
    ...(doneNow.length ? [t(doneNow.length === 1 ? 'counselDoneOne' : 'counselDoneMany').replace('{n}', String(doneNow.length))] : []),
    t(shown.length === 1 ? 'counselTipsOne' : 'counselTipsMany').replace('{n}', String(shown.length)),
  ].join(' · ');
  const bridge = narrowV;
  if (bridge && !(unfolded ?? tier <= 0)) {
    // The line (Nick, 11.10): who speaks, the first thing to do, how many — the map stays in view; a tap unfolds it.
    const first = shown[0];
    return (
      <div class="counsel folded">
        <button class={`counsel-line ${first ? `u${first.urgency}` : 'done'} ${first && fresh.has(first.id) ? 'fresh' : ''}`} aria-expanded="false" title={t('counselTitle')}
          onClick={() => { counselOpen.value = true; foldPanel.value++; }}>
          <span class="who">{personaShort(v.me.persona)}</span>
          <span class="what">{first ? first.title : <><b aria-hidden="true">✓</b> {doneNow.map((d) => d.title).join(', ')}</>}</span>
          <span class="tally">{tally} <i aria-hidden="true">▾</i></span>
        </button>
      </div>
    );
  }
  return (
    <div class={`counsel ${tier <= 1 ? 'first' : ''}`}>
      <small class="who"><span>{t(v.me.persona as 'vane')} · {t('counselTitle')}</span> <Constellation persona={v.me.persona} /> <span class="tally">{tally}</span>{bridge && <button class="fold" aria-expanded="true" title={t('counselFold')} onClick={() => { counselOpen.value = false; }}>▴</button>}</small>
      {doneNow.length > 0 && <p class="done-draw" role="status"><b aria-hidden="true">✓</b> {t('counselDoneDraw')} {doneNow.map((d) => d.title).join(', ')}</p>}
      {(bridge && !allCounsel ? shown.slice(0, 1) : shown).map((card) => (
        <div key={card.id} class={`card u${card.urgency} ${fresh.has(card.id) ? 'fresh' : ''}`}>
          <p><b>{card.title}</b> · {card.line}</p>
          <div class="acts">
            <button onClick={card.go}>{t('showMe')}</button>
            {card.hasCommand && (!spends(card.command) || card.id === firstDoIt) && <button class="primary" data-fx-target={commandTarget(card.command) ?? undefined} onClick={() => { const c = card; void c.run().then((ok) => { if (ok) { noteCounselTaken(); skippedCounsel.value = new Set([...skippedCounsel.value, c.id]); if (!c.voice) void act({ type: 'counsel_answer', id: c.id, taken: true }); } }); }}>{t('doIt')}</button>}
            <button class="link" onClick={() => answer(card.id, false, card.voice)}>{t('notNow')}</button>
          </div>
        </div>
      ))}
      {bridge && shown.length > 1 && <button class="link more" onClick={() => setAllCounsel(!allCounsel)}>{allCounsel ? t('counselFewer') : t('counselMore').replace('{n}', String(shown.length - 1))}</button>}
    </div>
  );
}

/** First minutes: objectives that tick themselves off as the player acts, each with a way in. */
const COACH_KEY = 'aurane.coach';
const coachDone = signal<number>((() => { try { return Number(localStorage.getItem(COACH_KEY) ?? 0); } catch { return 0; } })());
const enteredSystem = signal(false);
systemMode.subscribe((id) => { if (id) enteredSystem.value = true; });

/** Keeping the Colony (decision Nick, 30.09): the code confirming the address given at the founding, then, until the
 *  Colony has an e-mail or a passkey, a nudge to protect it once the first relay is out. A guest who lost their
 *  session is told their code found the Colony again. */
function AnchorBanner({ v }: { v: PlayerView }) {
  const [info, setInfo] = useState<AccountInfo | null>(null);
  const [pending, setPending] = useState<string | null>(() => { try { return localStorage.getItem('aurane.pendingEmail'); } catch { return null; } });
  const [later, setLater] = useState<boolean>(() => { try { return sessionStorage.getItem('aurane.protectLater') === '1'; } catch { return false; } });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [resumed, setResumed] = useState<string | null>(null);
  const load = (): void => { void fetchAccount().then(setInfo); };
  useEffect(() => {
    load();
    try {
      const back = sessionStorage.getItem('aurane.resumed');
      // #89: a 5 s toast was missed; the Colony found back by its code is said in a card that stays until read.
      if (back !== null) { sessionStorage.removeItem('aurane.resumed'); setResumed(back || v.me.name); }
      const refused = sessionStorage.getItem('aurane.emailRefused');
      if (refused) { sessionStorage.removeItem('aurane.emailRefused'); const r = JSON.parse(refused) as { email: string; reason: string }; toast.value = { text: t('foundEmailRefused').replace('{s}', r.email).replace('{r}', tError(r.reason)), kind: 'err' }; setTimeout(() => { toast.value = null; }, 9000); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [v.me.onboarding?.tier]);
  const protectedNow = !!info?.account?.email || (info?.passkeys.length ?? 0) > 0;
  const drop = (): void => { try { localStorage.removeItem('aurane.pendingEmail'); } catch { /* ignore */ } setPending(null); };
  if (pending && !info?.account?.email) {
    const confirm = async (e: Event) => {
      e.preventDefault(); setBusy(true); setErr(null);
      const r = await verifyEmail(code.trim());
      setBusy(false);
      if (r.ok) { drop(); load(); toast.value = { text: t('emailAdded'), kind: 'ok' }; } else setErr(tError(r.reason ?? ''));
    };
    return (
      <form class="card anchor" onSubmit={(e) => void confirm(e)}>
        <b>{t('pendingEmailTitle').replace('{s}', pending)}</b>
        <p class="muted small">{t('pendingEmailHelp')}</p>
        <div class="row"><input inputMode="numeric" autoComplete="one-time-code" value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} placeholder="123 456" maxLength={8} /><button class="primary" disabled={busy || code.trim().length < 6}>{t('confirmCode')}</button></div>
        {err && <p class="error small">{err}</p>}
        <p class="row"><button type="button" class="link" onClick={() => void startEmail(pending, lang.value)}>{t('resend')}</button> · <button type="button" class="link" onClick={drop}>{t('later')}</button></p>
      </form>
    );
  }
  if (resumed) return (
    <div class="card anchor resumed" role="status">
      <b>{t('resumedColony').replace('{s}', resumed)}</b>
      <p class="muted small">{t('resumedHelp')}</p>
      <div class="actions"><button class="primary" onClick={() => setResumed(null)}>{t('resumedOk')}</button></div>
    </div>
  );
  if (!info || protectedNow || later || (v.me.onboarding?.tier ?? 0) < 1) return null;
  return (
    <div class="card anchor protect">
      <b>{t('protectTitle')}</b>
      <p class="muted small">{t('protectHelp')}</p>
      <p class="row"><button class="primary" onClick={() => { tab.value = 'account'; openPanel.value++; }}>{t('protectGo')}</button> <button class="link" onClick={() => { try { sessionStorage.setItem('aurane.protectLater', '1'); } catch { /* ignore */ } setLater(true); }}>{t('later')}</button></p>
    </div>
  );
}

function Coach({ v }: { v: PlayerView }) {
  const done = useSig(coachDone);
  const entered = useSig(enteredSystem);
  const sel = useSig(selected);
  const owned = v.systems.filter((s) => s.owner === v.me.id);
  const built = owned.some((s) => (s.buildings?.length ?? 0) > 2 || (s.buildQueue?.length ?? 0) > 0);
  const steps: { text: string; why: string; met: boolean; go?: () => void }[] = [
    { text: t('coach1'), why: t('coach1Why'), met: sel !== null, go: () => { selected.value = v.me.capital; tab.value = 'system'; } },
    { text: t('coach2'), why: t('coach2Why'), met: v.me.connectedCount >= 2 || v.relays.some((r) => r.owner === v.me.id), go: () => { selected.value = v.me.capital; tab.value = 'system'; linkFrom.value = v.me.capital; } },
    { text: t('coach6'), why: t('coach6Why'), met: entered, go: () => { systemMode.value = v.me.capital; } },
    { text: t('coach7'), why: t('coach7Why'), met: built, go: () => { systemMode.value = v.me.capital; } },
    { text: t('coach8'), why: t('coach8Why'), met: (v.me.policy.notes ?? '').length > 0, go: () => { tab.value = 'general'; } },
    { text: t('coach3'), why: t('coach3Why'), met: (v.draw?.index ?? -1) >= 0 && v.me.lastProduced.metal > 0, go: () => { tab.value = 'logistics'; } },
  ];
  // Objectives are ordered; the first unmet one is shown, earlier ones count as done once met.
  const idx = Math.max(done, steps.findIndex((s) => !s.met));
  const current = idx < 0 || idx >= steps.length ? null : steps[idx]!;
  useEffect(() => {
    // Persist progress when the current objective becomes met.
    if (current?.met) { const n = idx + 1; coachDone.value = n; try { localStorage.setItem(COACH_KEY, String(n)); } catch { /* ignore */ } }
  }, [current?.met, idx]);
  if (!current) return null;
  const skip = () => { const n = idx + 1; coachDone.value = n; try { localStorage.setItem(COACH_KEY, String(n)); } catch { /* ignore */ } };
  return (
    <div class="coach">
      <span class="step">{idx + 1}/{steps.length}</span>
      <div class="text"><p>{current.text}</p><small>{current.why}</small></div>
      {current.go && <button class="primary" onClick={current.go}>{t('show')}</button>}
      <button onClick={skip} title={t('skip')}>✕</button>
    </div>
  );
}

let hudReceivedAt = Date.now();
view.subscribe(() => { hudReceivedAt = Date.now(); });

const isNarrow = (): boolean => window.innerWidth < 900;
/** When the panel last folded on request: a selection arriving with the fold (leaving a system) must not reopen it. */
let foldedAt = 0;
/** The Counsel on the Bridge: one line until tapped (null = automatic: unfolded only for the very first steps, tier 0). */
const counselOpen = signal<boolean | null>(null);
/** The Counsel's cards already unfolded once on the Bridge, per Colony: an urgent or new card opens the line by itself
 *  only the first time. Kept across reloads when the browser allows it, else for this page only. */
const SEEN_KEY = 'aurane.counselSeen';
let counselSeen: { colony: string; ids: Set<string> } | null = null;
function seenCounsel(colony: string): Set<string> {
  if (counselSeen?.colony !== colony) {
    let ids: unknown = [];
    try { ids = JSON.parse(localStorage.getItem(`${SEEN_KEY}.${colony}`) ?? '[]'); } catch { /* private mode: this page only */ }
    counselSeen = { colony, ids: new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : []) };
  }
  return counselSeen.ids;
}
function markCounselSeen(colony: string, id: string): void {
  const ids = seenCounsel(colony);
  if (ids.has(id)) return;
  ids.add(id);
  try { localStorage.setItem(`${SEEN_KEY}.${colony}`, JSON.stringify([...ids].slice(-80))); } catch { /* ignore */ }
}
/** A star tapped on the galaxy (the same one again included): on a phone the panel opens as a preview. */
const starTapped = signal(0);
/** Empty space tapped on the galaxy (QA 12.10): on a phone it never opens the panel, and it folds a preview — a sheet
 *  that would only say « Touche une étoile » is not worth a third of the screen. */
const emptyTapped = signal(0);

/** Decision 0014: two posts. The Table (wide screens: the General beside the map, the depth tools) and the Bridge
 *  (phones: one column, one decision at a time); in between, the layout everyone had. */
type Post = 'table' | 'mid' | 'bridge';
const postOf = (): Post => (typeof window === 'undefined' ? 'mid' : window.innerWidth >= 1360 ? 'table' : window.innerWidth < 700 ? 'bridge' : 'mid');
const post = signal<Post>(postOf());
/** Below 900 px (the Bridge and the narrow « mid », 700-899) the HUD is compact: one row of resources with the « ? »
 *  pinned, « Protège ta Colonie » on one line, the Counsel as its line (QA 12.10: at 800 px the HUD covered 68 % of
 *  the map). The posts' other differences (tabs, dock, memory card) stay as decided in 0014. */
const narrow = signal(typeof window !== 'undefined' && isNarrow());
if (typeof window !== 'undefined') window.addEventListener('resize', () => { const p = postOf(); if (p !== post.value) post.value = p; if (isNarrow() !== narrow.value) narrow.value = isNarrow(); });

function Panel({ v, map }: { v: PlayerView; map: { current: GalaxyMap | null } }) {
  const [open, setOpen] = useState(() => !isNarrow());
  // Nick 12.10: a star tapped on a phone opens the panel as a preview (name, owner, Enter / Link) and the map keeps at
  // least half the screen; the grip, the tab strip or a tab makes it full height.
  const [peek, setPeek] = useState(false);
  const [more, setMore] = useState(false);
  const current = useSig(tab);
  const sel = useSig(selected);
  // On a phone the panel stays folded until something is selected or a tab is tapped: the map comes first.
  useEffect(() => { if (sel && isNarrow() && Date.now() - foldedAt > 400) setOpen(true); }, [sel]);
  const tapped = useSig(starTapped);
  useEffect(() => { if (tapped > 0 && isNarrow() && !open && Date.now() - foldedAt > 400) { setOpen(true); setPeek(true); } }, [tapped]);
  useEffect(() => { if (!open) setPeek(false); }, [open]);
  const emptied = useSig(emptyTapped);
  useEffect(() => { if (emptied > 0 && isNarrow() && open && (peek || tab.value === 'system')) { foldedAt = Date.now(); setOpen(false); } }, [emptied]);
  const openReq = useSig(openPanel);
  useEffect(() => { if (openReq > 0) { setOpen(true); setPeek(false); } }, [openReq]);
  const foldReq = useSig(foldPanel);
  useEffect(() => { if (foldReq > 0 && isNarrow()) { foldedAt = Date.now(); setOpen(false); } }, [foldReq]);
  // On the Bridge the Counsel and the panel never share the screen: the panel opens, the Counsel folds to its line.
  useEffect(() => { if (open && narrow.value) counselOpen.value = false; }, [open]);
  // Progressive onboarding: a tab appears with its tier (docs/design/ONBOARDING-S0.md), the General says why.
  const tier = v.me.onboarding?.tier ?? 6;
  const TAB_TIER: Partial<Record<Tab, number>> = { logistics: 1, market: 2, fleets: 3, diplomacy: 5 };
  // « I know the game » shows every tab; what a tab offers still waits for its tier (greyed, with the reason).
  const skipped = v.me.onboarding?.skipped === true;
  const postV = useSig(post);
  // Decision 0014: on the Table the General lives in the dock and « Prévoir » (projection, places of memory) is a tab.
  const all: Tab[] = (['colony', 'system', 'plan', 'logistics', 'market', 'fleets', 'diplomacy', 'general', 'log', 'account'] as Tab[]).filter((k) => (skipped || (TAB_TIER[k] ?? 0) <= tier) && !(k === 'general' && postV === 'table') && !(k === 'plan' && postV !== 'table'));
  const primary: Tab[] = ['colony', 'system', 'general', 'log'];
  useEffect(() => { if (!all.includes(current)) tab.value = 'colony'; }, [tier, postV]);
  const pendingV = useSig(pendingDoctrine);
  useEffect(() => { loadPendingDoctrine(); }, []);
  const read = useSig(logRead);
  const unread = current === 'log' ? 0 : v.events.filter((e) => e.at > read && e.kind !== 'draw').length;
  useEffect(() => { if (current === 'log') markLogRead(v); }, [current, v.events.length]);
  const tabs: Tab[] = isNarrow() && !more ? [...primary, ...(primary.includes(current) ? [] : [current])] : all;
  const labels: Record<Tab, string> = { colony: postV === 'bridge' ? t('tabToday') : t('tabColony'), system: t('tabSystem'), logistics: t('tabLogistics'), market: t('tabMarket'), fleets: t('tabFleets'), diplomacy: t('tabDiplomacy'), general: t('tabGeneral'), plan: t('tabPlan'), log: t('tabLog'), account: t('tabAccount') };
  return (
    <div class={`panel ${open ? 'open' : ''} ${open && peek ? 'peek' : ''}`}>
      {open && isNarrow() && <button class="grip" aria-expanded={!peek} aria-label={peek ? t('panelExpand') : t('panelPeek')} title={peek ? t('panelExpand') : t('panelPeek')} onClick={() => setPeek(!peek)}><i aria-hidden="true" /></button>}
      <div class="tabs" onClick={() => { setOpen(true); setPeek(false); }}>
        {tabs.map((k) => <button key={k} class={current === k ? 'on' : ''} onClick={(e) => { e.stopPropagation(); tab.value = k; setOpen(true); setPeek(false); setMore(false); }}>{labels[k]}{k === 'log' && unread > 0 ? <>{' '}<i class="badge" title={t('unreadN').replace('{n}', String(unread))} aria-label={t('unreadN').replace('{n}', String(unread))}>{unread > 9 ? '9+' : unread}</i></> : null}{k === 'general' && pendingV && current !== 'general' ? <i class="dotn" /> : null}</button>)}
        {isNarrow() && <button class={more ? 'on' : ''} onClick={(e) => { e.stopPropagation(); setMore(!more); }} title={t('more')}>⋯</button>}
        <button class="collapse" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>{open ? '▾' : '▴'}</button>
      </div>
      <div class="body">
        {current === 'colony' && <ColonyPanel v={v} map={map} />}
        {current === 'system' && <SystemPanel v={v} map={map} />}
        {current === 'logistics' && <LogisticsPanel v={v} onCenter={(id) => { selected.value = id; map.current?.centerOn(id); }} />}
        {current === 'market' && <MarketPanel v={v} />}
        {current === 'fleets' && <FleetsPanel v={v} map={map} />}
        {current === 'diplomacy' && <DiplomacyPanel v={v} />}
        {current === 'general' && <GeneralPanel v={v} />}
        {current === 'plan' && <div class="plan"><Projection v={v} /><Places v={v} map={map} /></div>}
        {current === 'log' && <LogPanel v={v} />}
        {current === 'account' && <AccountPanel v={v} />}
      </div>
    </div>
  );
}

/** The capital, whatever was selected last (a relay just built leaves the panel on the new star): selected and
 *  centred, or its plateau opened straight away. */
function goCapital(v: PlayerView, map: GalaxyMap | null, enter = false): void {
  selected.value = v.me.capital;
  tab.value = 'system';
  map?.centerOn(v.me.capital);
  if (enter) systemMode.value = v.me.capital;
}

function ColonyPanel({ v, map }: { v: PlayerView; map: { current: GalaxyMap | null } }) {
  const mine = v.systems.filter((s) => s.owner === v.me.id);
  const capName = v.systems.find((s) => s.id === v.me.capital)?.name ?? v.me.capital;
  // The General's latest words on the first screen (playtest 09.10: its first contact was a tutorial card, and the
  // thread sat behind a tab nobody opened). The awakening line is written by the first counsel: read it a beat later.
  useEffect(() => {
    if (talkLoaded) return;
    const timer = setTimeout(() => { if (talkLoaded) return; talkLoaded = true; void fetchTalk().then((h) => { if (h.length) talk.value = h; }); }, 1500);
    return () => clearTimeout(timer);
  }, []);
  const said = [...talk.value].reverse().find((x) => x.who === 'general');
  return (
    <div>
      <h2>{v.me.name} <small class={`f-${v.me.faction}`}>{t(v.me.faction as 'guild')}</small></h2>
      <p>{t(v.me.persona as 'vane')}{v.me.watching ? ` · ${t('watching')}` : ''}</p>
      {said && post.value !== 'table' && <blockquote class="said" onClick={() => { tab.value = 'general'; }}>« {said.text} » <button type="button" class="link">{t('answerGeneral')}</button></blockquote>}
      {(archivesData.value?.echoes ?? 0) > 0 && archivesData.value?.seance && (
        <p class="echo-line"><span>{t('echoesHeld').replace('{n}', String(archivesData.value?.echoes ?? 0))}</span> <button class="echo" onClick={() => void apiOpenSeance(v.me.capital, lang.value).then((r) => { if (r.seance) seanceV.value = r.seance; })}>{t('listenEchoHere')}</button></p>
      )}
      <div class="actions"><button class="enter" onClick={() => goCapital(v, map.current, true)}>◎ {t('enterSystem')} · {capName} ★</button></div>
      <ul class="list">
        {mine.map((s) => (
          <li key={s.id} onClick={() => { selected.value = s.id; tab.value = 'system'; map.current?.centerOn(s.id); }}>
            <span class={`dot r-${s.resource}`} /> {s.name} {s.id === v.me.capital ? '★' : ''} <small>{s.connected ? '●' : '○'} {t('band')} {s.band}</small>
            <small>{(s.buildings ?? []).map((b) => t(b)).join(', ')}</small>
          </li>
        ))}
      </ul>
      {v.ended && <p class="tag">{v.ended.reason === 'silence' ? t('ended') : t('renaissance')}</p>}
      {post.value === 'bridge' ? <details class="decrees-fold"><summary>{t('decrees')}</summary><Decrees v={v} /></details> : <Decrees v={v} />}
    </div>
  );
}

/** Decrees: three public, temporary bonuses bought with Credits (GDD § 8). */
function Decrees({ v }: { v: PlayerView }) {
  const now = v.time;
  const desc = { range: t('decreeRangeDesc'), freefees: t('decreeFreefeesDesc'), longwatch: t('decreeLongwatchDesc') } as const;
  const tv = useSig(teach);
  return (
    <div class="decrees">
      <h3>{t('decrees')} <small>{Math.floor(v.me.credits)} {t('credits')}</small></h3>
      <p class="muted small">{t('decreesHint')}</p>
      <div class="cards">
        {DECREES.map((k) => {
          const active = v.me.decrees.find((d) => d.kind === k && d.until > now);
          const cost = DECREE_COST_CREDITS[k];
          return (
            <button key={k} class={`card-btn ${active ? 'has' : ''} ${teachClass(tv, `decree:${k}`)}`} disabled={!!active || v.me.credits < cost} onClick={() => void act({ type: 'decree', kind: k })}>
              <b>{decreeLabel(k)}</b><small>{desc[k]}</small>
              {active ? <small class="ok">{t('inForce')} · {hms(active.until - now)} {t('remaining')}</small> : <small class="r-credits">{cost} {t('credits')} · {k === 'freefees' ? t('decreeDraws').replace('{n}', String(DECREE_HOURS[k])) : `${DECREE_HOURS[k]} h`}</small>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SystemPanel({ v, map }: { v: PlayerView; map: { current: GalaxyMap | null } }) {
  const sel = useSig(selected);
  const tv = useSig(teach);
  const s = v.systems.find((x) => x.id === sel);
  const capName = v.systems.find((x) => x.id === v.me.capital)?.name ?? v.me.capital;
  const toCapital = <button class="capjump" onClick={() => goCapital(v, map.current)} title={t('capitalTag')}>★ {capName}</button>;
  if (!s) return <div><p class="muted">{t('selectHint')}</p>{toCapital}</div>;
  const mine = s.owner === v.me.id;
  const ownerName = s.owner ? v.colonies.find((c) => c.id === s.owner)?.name ?? s.owner : t('unclaimed');
  const totalSlots = s.slots + (s.id === v.me.capital ? 3 : 0);
  const free = s.buildings ? totalSlots - s.buildings.length : 0;
  const fleetsHere = v.fleets.filter((f) => f.at === s.id && f.owner === v.me.id);
  const drawn = v.draw?.bands.includes(s.band) ?? false;
  const targets = v.linkTargets[s.id]?.length ?? 0;
  const linking = useSig(linkFrom) === s.id;
  return (
    <div>
      {s.id !== v.me.capital && toCapital}
      <h2><span class={`r-${s.resource}`}><Icon name={s.resource} size={18} /></span> {s.name} {s.id === v.me.capital && <span class="tag">{t('capitalTag')}</span>} {s.kind === 'pulsar' && <span class="tag">{t('kindPulsar')}</span>} {s.kind === 'beacon' && <span class="tag">{t('kindBeacon')}</span>}</h2>
      <div class="facts">
        <span><b class={`r-${s.resource}`}>{t(s.resource)}</b> · {t(`${s.resource}Desc`)}</span>
        <span class="own">{t('band')} <b class={drawn ? 'ev-eruption' : ''}>{s.band}</b>{drawn ? ` ×3 ${t('yieldNow')}` : ''} · {t('slots')} <b>{totalSlots}</b> · {t('owner')}: <b>{ownerName}</b>{s.connected ? ' ●' : ''}</span>
        <span>{t(TPL_KEY[s.template] ?? 'tplForge')} · <b>{s.pois}</b> {t('bodies')}{s.signature ? <> · <b class="bad">{t('hiddenOwner')}</b></> : null}</span>
        {s.population !== null && <span>{t('population')} <b>{(s.population * 100).toFixed(0)} %</b></span>}
        {s.stationHp !== null && <span>{t('station')} <b>{Math.round(s.stationHp)}</b> / 300{s.engaged ? ` · ${t('underAttack')}` : ''}</span>}
        {s.wreck && expeditionsOn.value && <span class="wreckhere">⚓ <b>{t('poiWreck')}</b> · {t('wreckHere')}</span>}
        {s.stock && <span class="localstock">{t('localStock')} : {RES.map((r) => <b key={r} class={`r-${r}`} title={t(r)}><Icon name={r} size={13} />{Math.round(s.stock![r])}</b>)} <small>/ {s.capacity}</small></span>}
      </div>
      <div class="actions">
        <button class={`enter ${s.engaged ? 'hot' : ''} ${teachClass(tv, `enter:${s.id}`)}`} onClick={() => { systemMode.value = s.id; }}>◎ {t('enterSystem')}</button>
        <button class="primary" onClick={() => { linkFrom.value = linking ? null : s.id; }} disabled={!s.connected || targets === 0}>{t('linkMode')} {s.connected ? `(${targets} ${t('inRange')})` : ''}</button>
        <BeaconControls v={v} s={s} />
        {mine && s.connected && (archivesData.value?.echoes ?? 0) > 0 && archivesData.value?.seance && (
          <button class="echo" onClick={() => void apiOpenSeance(s.id, lang.value).then((r) => { if (r.seance) seanceV.value = r.seance; else if (r.reason) toast.value = { text: tError(r.reason), kind: 'err' }; })}>{t('listenEcho').replace('{n}', String(archivesData.value?.echoes ?? 0))}</button>
        )}
        {!mine && probeOpen(v) && <button disabled={v.me.influence < AGENT_COST_INFLUENCE.probe} onClick={() => void act({ type: 'agent_mission', mission: 'probe', target: s.id })}>{t('probe')} ★{AGENT_COST_INFLUENCE.probe}</button>}
        {s.lit && <span class="tag">{t('lit')} {t('by')} {v.colonies.find((c) => c.id === s.lit!.by)?.name}</span>}
      </div>
      <AncientRelays v={v} s={s} />
      {linking && <LinkTargets v={v} from={s.id} />}
      {mine && !s.connected && (() => {
        // Waiting for its relay (issue #48): the countdown the map shows on the dashed line, in words.
        const r = v.relays.filter((x) => x.owner === v.me.id && !x.ready && !x.cut && (x.a === s.id || x.b === s.id)).sort((x, y) => x.readyAt - y.readyAt)[0];
        return r ? <LiveEta readyAt={r.readyAt} v={v} /> : null;
      })()}
      {mine && s.buildings && <p class="muted small">{t('buildHint')} · {free > 0 ? `${free}/${totalSlots} ${t('slots').toLowerCase()}` : t('noSlot')}</p>}
      <ScoutActions v={v} s={s} />
      {fleetsHere.length > 0 && <FleetList v={v} fleets={fleetsHere} />}
      {!mine && s.owner && <SystemHostile v={v} s={s} />}
    </div>
  );
}

/** Link mode: the systems a relay can reach from here, with their cost. Tap a row to build; no need to find the star. */
function LinkTargets({ v, from }: { v: PlayerView; from: string }) {
  const cands = (v.linkTargets[from] ?? []).map((c) => ({ ...c, sys: v.systems.find((x) => x.id === c.to) })).filter((c) => c.sys);
  const stock = v.systems.find((x) => x.id === from)?.stock ?? v.me.stock;
  const tv = useSig(teach);
  return (
    <div class="linktargets">
      <h3>{t('targetsInRange')} <small>{cands.length}</small></h3>
      {cands.length === 0 && <p class="muted small">{t('noTargets')}</p>}
      <ExpandHelp open={cands.length === 0} />
      <ul class="list">
        {cands.map((c) => {
          const afford = c.metal <= Math.max(stock.metal, v.me.stock.metal) && c.energy <= Math.max(stock.energy, v.me.stock.energy);
          return (
            <li key={c.to}>
              <span class={`dot r-${c.sys!.resource}`} /> <b>{c.sys!.name}</b> <small>{t(c.sys!.resource)} · {t('band')} {c.sys!.band}</small>
              <span class="cost"><span class="r-metal"><Icon name="metal" size={12} />{c.metal}</span><span class="r-energy"><Icon name="energy" size={12} />{c.energy}</span></span>
              <button class={`primary ${teachClass(tv, `link:${c.to}`)}`} disabled={!afford} onClick={() => { linkFrom.value = null; void act({ type: 'build_relay', a: from, b: c.to }); }}>{t('linkTo')}</button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The scout (issue #48): « send the scout here » from any star not ours, and the wormhole found at this star. */
function ScoutActions({ v, s }: { v: PlayerView; s: SystemView }) {
  const scouts = v.fleets.filter((f) => f.owner === v.me.id && f.scout && f.at !== null && f.at !== s.id && f.order === 'idle');
  const hole = (v.me.wormholes ?? []).find((h) => h.a === s.id || h.b === s.id);
  const other = hole ? (hole.a === s.id ? hole.b : hole.a) : null;
  const here = hole ? v.fleets.filter((f) => f.owner === v.me.id && f.at === s.id && f.order === 'idle') : [];
  const name = (id: string): string => v.systems.find((x) => x.id === id)?.name ?? id;
  if (!(s.owner !== v.me.id && scouts.length) && !hole) return null;
  return (
    <div class="scout-actions">
      {s.owner !== v.me.id && scouts.length > 0 && <button class="primary" data-fx-target={s.id} onClick={() => void act({ type: 'fleet_order', fleet: scouts[0]!.id, order: 'move', target: s.id }, t('scoutSent').replace('{s}', s.name))}>{t('sendScout')}</button>}
      {hole && other && (
        <p class="wormhole small">{t('wormholeHere').replace('{s}', name(other)).replace('{t}', minutesText((hole.closesAt - v.time) / Math.max(1, v.timeScale)))}
          {here.map((f) => <button key={f.id} onClick={() => void act({ type: 'fleet_order', fleet: f.id, order: 'move', target: other })}>{t('crossWormhole')}{f.scout ? ` (${t('scout')})` : ''}</button>)}
        </p>
      )}
    </div>
  );
}

function SystemHostile({ v, s }: { v: PlayerView; s: SystemView }) {
  const idle = v.fleets.filter((f) => f.owner === v.me.id && f.at !== null && f.order === 'idle' && f.combat > 0);
  const relays = v.relays.filter((r) => r.owner === s.owner && (r.a === s.id || r.b === s.id) && r.ready && !r.cut);
  return (
    <div>
      {idle.length > 0 && (
        <div class="actions">
          <button onClick={() => void act({ type: 'fleet_order', fleet: idle[0]!.id, order: 'blockade', target: s.id })}>{t('blockade')}</button>
          {s.owner && <button onClick={() => void act({ type: 'fleet_order', fleet: idle[0]!.id, order: 'raid', target: s.id })}>{t('raid')}</button>}
        </div>
      )}
      <h3>{t('agents')} <small>{Math.floor(v.me.influence)} {t('influence')}</small></h3>
      <div class="cards">
        <button class="card-btn" disabled={v.me.influence < AGENT_COST_INFLUENCE.spy} onClick={() => void act({ type: 'agent_mission', mission: 'spy', target: s.sector })}><b>{t('spy')}</b><small>{t('spyDesc')}</small><small class="r-influence">★ {AGENT_COST_INFLUENCE.spy}</small></button>
        {relays[0] && <button class="card-btn" disabled={v.me.influence < AGENT_COST_INFLUENCE.sabotage} onClick={() => void act({ type: 'agent_mission', mission: 'sabotage', target: relays[0]!.id })}><b>{t('sabotage')}</b><small>{t('sabotageDesc')}</small><small class="r-influence">★ {AGENT_COST_INFLUENCE.sabotage}</small></button>}
        {s.owner && <button class="card-btn" disabled={v.me.influence < AGENT_COST_INFLUENCE.envoy} onClick={() => void act({ type: 'agent_mission', mission: 'envoy', target: s.owner! })}><b>{t('envoy')}</b><small>{t('envoyDesc')}</small><small class="r-influence">★ {AGENT_COST_INFLUENCE.envoy}</small></button>}
      </div>
    </div>
  );
}

function FleetList({ v, fleets }: { v: PlayerView; fleets: PlayerView['fleets'] }) {
  return (
    <ul class="list">
      {fleets.filter((f) => (f.units ? unitLine(f.units, f.size) !== '' : f.size > 0)).map((f) => (
        <li key={f.id}>
          <b>{unitLine(f.units, f.size)}</b> · {orderLabel(f.order)}
          {f.at ? ` ${t('at')} ${v.systems.find((s) => s.id === f.at)?.name ?? f.at}` : ` ${t('inTransit')}`}
          {f.owner === v.me.id && f.at && f.order !== 'blockade' && selected.value && selected.value !== f.at && (
            <span class="actions inline">
              <button onClick={() => void act({ type: 'fleet_order', fleet: f.id, order: 'move', target: selected.value! })}>{t('move')}</button>
              <button onClick={() => void act({ type: 'fleet_order', fleet: f.id, order: 'defend', target: selected.value! })}>{t('defend')}</button>
            </span>
          )}
          {f.owner === v.me.id && f.at && f.at !== v.me.capital && <button onClick={() => void act({ type: 'fleet_order', fleet: f.id, order: 'return', target: v.me.capital })}>{t('return')}</button>}
        </li>
      ))}
    </ul>
  );
}

function FleetsPanel({ v, map }: { v: PlayerView; map: { current: GalaxyMap | null } }) {
  const mine = v.fleets.filter((f) => f.owner === v.me.id);
  if (!mine.length) return <p class="muted">—</p>;
  return (
    <div>
      <FleetList v={v} fleets={mine} />
      <p class="muted" onClick={() => mine[0]?.at && map.current?.centerOn(mine[0].at)}>{t('selectHint')}</p>
    </div>
  );
}

function MarketPanel({ v }: { v: PlayerView }) {
  const [region, setRegion] = useState(v.me.regions[0]?.key ?? '');
  const [res, setRes] = useState<Resource>('food');
  const [side, setSide] = useState<'buy' | 'sell'>('sell');
  const [qty, setQty] = useState(20);
  const [price, setPrice] = useState(1);
  const tv = useSig(teach);
  const pre = useSig(marketPrefill);
  useEffect(() => { if (!pre) return; setRegion(pre.region); setRes(pre.resource); setSide(pre.side); setQty(pre.qty); setPrice(pre.price); marketPrefill.value = null; }, [pre]);
  if (!v.me.regions.length) return <p class="muted">{t('noMarket')}</p>;
  const last = v.clearing.find((c) => c.region === region && c.resource === res);
  // The last price however old, else the reference (playtest 28.09: only dashes after five Draws).
  const priceCell = (rg: string, r: Resource) => { const p = v.prices?.find((x) => x.region === rg && x.resource === r); if (!p) return <span class="muted">{fill(t('priceRef'), { p: BASE_PRICE[r] })}</span>; const age = Math.max(0, Math.floor((v.time - p.at) / 3600)); return <span title={age ? fill(t('priceAge'), { n: age }) : ''}>{p.price}{age ? <span class="muted"> ·{age}</span> : null}</span>; };
  const refNow = BASE_PRICE[res];
  return (
    <div>
      <h3>{t('lastPrice')}</h3>
      <table class="prices"><thead><tr><th>{t('region')}</th>{RES.map((r) => <th key={r} class={`r-${r}`}><Icon name={r} size={12} /></th>)}</tr></thead>
        <tbody>{v.me.regions.map((rg) => <tr key={rg.key} class={rg.key === region ? 'on' : ''} onClick={() => setRegion(rg.key)}><td>{rg.name}</td>{RES.map((r) => { const c = v.clearing.find((x) => x.region === rg.key && x.resource === r); return <td key={r}>{c ? c.price : priceCell(rg.key, r)}</td>; })}</tr>)}</tbody></table>
      <div class="row">
        <label>{t('region')}<select value={region} onChange={(e) => setRegion((e.target as HTMLSelectElement).value)}>{v.me.regions.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}</select></label>
        <label>{t(res)}<select value={res} onChange={(e) => setRes((e.target as HTMLSelectElement).value as Resource)}>{RES.map((r) => <option key={r} value={r}>{t(r)}</option>)}</select></label>
      </div>
      <div class="seg wide"><button class={side === 'buy' ? 'on' : ''} onClick={() => setSide('buy')}>{t('buy')}</button><button class={side === 'sell' ? 'on' : ''} onClick={() => setSide('sell')}>{t('sell')}</button></div>
      <div class="row">
        <label>{t('qty')}<input type="number" min={1} value={qty} onInput={(e) => setQty(Number((e.target as HTMLInputElement).value))} /></label>
        <label>{t('price')}<input type="number" min={0.1} step={0.1} value={price} onInput={(e) => setPrice(Number((e.target as HTMLInputElement).value))} /></label>
        <button class={`primary ${teachClass(tv, 'market:place')}`} onClick={() => void act({ type: 'market_order', region, resource: res, side, qty, price })}>{t('place')}</button>
      </div>
      {last && <p class="muted">{t('lastPrice')}: {last.price} ({last.qty})</p>}
      <p class="muted">{fill(t('marketFloor'), { q: MAKER_QTY, r: t(res), b: Math.round(refNow * MAKER_BUY_MULT * 100) / 100, s: Math.round(refNow * MAKER_SELL_MULT * 100) / 100 })}</p>
      <h3>{t('myOrders')}</h3>
      <ul class="list">{v.orders.map((o) => <li key={o.id}>{t(o.side)} {o.qty} {t(o.resource)} @ {o.price} <button onClick={() => void act({ type: 'cancel_order', order: o.id })}>{t('cancel')}</button></li>)}</ul>
      <Barter v={v} />
    </div>
  );
}

function Barter({ v }: { v: PlayerView }) {
  const partners = v.colonies.filter((c) => c.id !== v.me.id).sort((a, b) => (b.ally ? 1 : 0) - (a.ally ? 1 : 0) || b.score - a.score).slice(0, 40);
  const [to, setTo] = useState(partners[0]?.id ?? '');
  const [giveR, setGiveR] = useState<Resource>('food');
  const [giveQ, setGiveQ] = useState(20);
  const [wantR, setWantR] = useState<Resource>('energy');
  const [wantQ, setWantQ] = useState(10);
  const name = (id: string): string => v.colonies.find((c) => c.id === id)?.name ?? id;
  const fmtStock = (st: Partial<Record<Resource, number | undefined>>): string => RES.filter((r) => st[r]).map((r) => `${st[r]} ${t(r)}`).join(' + ');
  return (
    <div>
      <h3>{t('barter')}</h3>
      <div class="row">
        <label>{t('offerTo')}<select value={to} onChange={(e) => setTo((e.target as HTMLSelectElement).value)}>{partners.map((c) => <option key={c.id} value={c.id}>{c.name}{c.ally ? ' ✓' : ''}{c.npc ? ' (PNJ)' : ''}</option>)}</select></label>
      </div>
      <div class="row">
        <label>{t('give')}<div class="seg"><input type="number" min={1} value={giveQ} onInput={(e) => setGiveQ(Number((e.target as HTMLInputElement).value))} /><select value={giveR} onChange={(e) => setGiveR((e.target as HTMLSelectElement).value as Resource)}>{RES.map((r) => <option key={r} value={r}>{t(r)}</option>)}</select></div></label>
        <label>{t('want')}<div class="seg"><input type="number" min={1} value={wantQ} onInput={(e) => setWantQ(Number((e.target as HTMLInputElement).value))} /><select value={wantR} onChange={(e) => setWantR((e.target as HTMLSelectElement).value as Resource)}>{RES.map((r) => <option key={r} value={r}>{t(r)}</option>)}</select></div></label>
        <button class="primary" disabled={!to} onClick={() => void act({ type: 'barter_offer', to, give: { [giveR]: giveQ }, want: { [wantR]: wantQ } })}>{t('send')}</button>
      </div>
      <p class="muted">{t('settledAtDraw')}</p>
      {v.barters.length > 0 && <h3>{t('offers')}</h3>}
      <ul class="list">
        {v.barters.map((b) => (
          <li key={b.id}>
            <span>{name(b.from)} → {name(b.to)}: <b>{fmtStock(b.give)}</b> ⇄ <b>{fmtStock(b.want)}</b> {b.accepted ? '✓' : ''}</span>
            {b.to === v.me.id && !b.accepted && <button class="primary" onClick={() => void act({ type: 'barter_accept', offer: b.id })}>{t('accept')}</button>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DiplomacyPanel({ v }: { v: PlayerView }) {
  const [name, setName] = useState('');
  const others = v.colonies.filter((c) => c.id !== v.me.id).sort((a, b) => b.score - a.score).slice(0, 30);
  const tv = useSig(teach);
  return (
    <div>
      <h3>{t('alliance')}</h3>
      {v.me.alliance ? <p>{v.colonies.find((c) => c.id === v.me.id)?.allianceName}</p> : (
        <div class="row"><input placeholder={t('createAlliance')} value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} /><button onClick={() => void act({ type: 'alliance_create', name })} disabled={name.length < 2}>{t('createAlliance')}</button></div>
      )}
      {v.invites.map((i) => <p key={i.alliance}>{i.name} <button onClick={() => void act({ type: 'alliance_join', alliance: i.alliance })}>{t('join')}</button></p>)}
      <h3>{t('treaties')}</h3>
      <ul class="list">{v.treaties.map((tr) => <li key={tr.id}>{t(tr.kind as 'nap')} — {v.colonies.find((c) => c.id === tr.with)?.name}</li>)}</ul>
      <ul class="list">
        {others.map((c) => (
          <li key={c.id}>
            <span class={`f-${c.faction}`}>{c.name}</span> <small>{c.score} {c.npc ? '· PNJ' : ''} {c.ally ? '· ✓' : ''}</small>
            <span class="actions inline">
              {(['nap', 'trade', 'transit'] as const).map((k) => <button key={k} class={teachClass(tv, `treaty:${k}:${c.id}`)} onClick={() => void act({ type: 'treaty', with: c.id, kind: k })}>{t(k)}</button>)}
              {v.me.alliance && <button onClick={() => void act({ type: 'alliance_invite', colony: c.id })}>+</button>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const talk = signal<Turn[]>([]);
/** The General asked back about a doctrine and holds none for the player's yes: until the next message. */
const awaitingDoctrine = signal(false);
let talkLoaded = false;
/** The player wrote to the General since this page opened: the composer's example gives way to a neutral invite. */
let sentThisSession = false;
view.subscribe((v) => { if (!v) sentThisSession = false; }); // logged out: another Colony starts afresh
/** The doctrine waiting for the player's yes (DOCTRINE_CONFIRM): restored from the server once per load. */
const pendingDoctrine = signal<PendingCard | null>(null);
let pendingLoaded = false;
view.subscribe((v) => { if (!v) { pendingLoaded = false; pendingDoctrine.value = null; awaitingDoctrine.value = false; } }); // logged out: the next Colony loads its own
function loadPendingDoctrine(force = false): void {
  if (pendingLoaded && !force) return;
  pendingLoaded = true;
  void fetchPendingDoctrine().then((b) => { pendingDoctrine.value = cardFromPending(b); });
}

/** « Voici ce que je ferai en ton absence »: the lines the General will play by, and the player's yes or no. */
function DoctrineCard({ v, card }: { v: PlayerView; card: PendingCard }) {
  const [busy, setBusy] = useState(false);
  const say = (text: string, kind: 'ok' | 'err' = 'ok'): void => { toast.value = { text, kind }; setTimeout(() => { if (toast.value?.text === text) toast.value = null; }, 3500); };
  const apply = async (): Promise<void> => {
    setBusy(true);
    const r = await confirmDoctrine(card.id);
    setBusy(false);
    if (r === 'ok') { pendingDoctrine.value = null; say(t('pendingApplied')); }
    else if (r === 'gone') { say(t('pendingGone'), 'err'); loadPendingDoctrine(true); }
    else say(t('generalOffline'), 'err');
  };
  const notThat = async (): Promise<void> => {
    setBusy(true);
    const ok = await discardDoctrine();
    setBusy(false);
    if (!ok) { say(t('generalOffline'), 'err'); return; }
    pendingDoctrine.value = null;
    say(t('pendingDiscarded'));
    (document.querySelector('.generalpanel .say textarea') as HTMLTextAreaElement | null)?.focus();
  };
  // On a phone the buttons come right under the title, before the lines, and the list folds after a few lines: the
  // yes and the no stay in view above the composer (390x844), the rest is one tap away.
  const [all, setAll] = useState(false);
  useEffect(() => setAll(false), [card.id]);
  const { shown, hidden } = visibleLines(card.readable, all);
  return (
    <div class="doctrine-card" role="region" aria-label={t('pendingTitle')}>
      <small class="who">{t(v.me.persona as 'vane')}</small>
      <p class="title"><b>{t('pendingTitle')}</b></p>
      <p class="voice">{t(pendingLineKey(v.me.persona))}</p>
      {card.question && <p class="question">{card.question}</p>}
      <div class="acts">
        <button class="primary" disabled={busy} onClick={() => void apply()}>{t('pendingApply')}</button>
        <button disabled={busy} onClick={() => void notThat()}>{t('pendingNotThat')}</button>
      </div>
      {card.readable.length > 0 ? (
        <>
          <ul>{shown.map((l, i) => <li key={i}>{l}</li>)}</ul>
          {hidden > 0 && <button class="link more" onClick={() => setAll(true)}>{t('pendingSeeAll').replace('{n}', String(hidden))}</button>}
          {all && card.readable.length > CARD_LINES + 1 && <button class="link more" onClick={() => setAll(false)}>{t('pendingSeeLess')}</button>}
        </>
      ) : <p class="muted small">{t('pendingNothing')}</p>}
    </div>
  );
}

function GeneralPanel({ v }: { v: PlayerView }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const thread = useSig(talk);
  const card = useSig(pendingDoctrine);
  const awaiting = useSig(awaitingDoctrine);
  const endRef = useRef<HTMLDivElement>(null);
  const p = v.me.policy;
  useEffect(() => { if (!talkLoaded) { talkLoaded = true; void fetchTalk().then((h) => { if (h.length) talk.value = h; }); } loadPendingDoctrine(); }, []);
  // The General speaks first when a hostile fleet heads our way: pick up its line when such an event lands.
  const inboundCount = v.events.filter((e) => e.kind === 'fleet.inbound' && e.actors[1] === v.me.id).length;
  // Whenever the General may have spoken first (hostile fleet, new tier, first contact), the thread is refetched: the
  // view push carries the event, not the words.
  const spokeCount = v.events.filter((e) => (e.kind === 'fleet.inbound' && e.actors[1] === v.me.id) || ((e.kind === 'onboarding.unlocked' || e.kind === 'contact.first') && e.actors[0] === v.me.id)).length;
  useEffect(() => {
    if (!talkLoaded || (spokeCount === 0 && inboundCount === 0)) return;
    const timer = setTimeout(() => void fetchTalk().then((h) => { const last = talk.value[talk.value.length - 1]; if (h.length && (h.length !== talk.value.length || h[h.length - 1]!.at !== last?.at)) talk.value = h; }), 300);
    return () => clearTimeout(timer);
  }, [spokeCount, inboundCount]);
  // What the General did; the player's answers to the Counsel are the player's, they live in the memory.
  const journal = v.me.journal.filter((n) => !n.kind.startsWith('counsel.')).reverse().slice(0, 12);
  const tv = useSig(teach);
  const cardRef = useRef<HTMLDivElement>(null);
  // A new doctrine card opens on its title (« Voici ce que je ferai… »), not on its last line; otherwise follow the thread.
  // The newest line in view. On the Table the conversation scrolls inside itself: scrolling into view moved the whole
  // dock and hid the General's header and counsel (QA 10.10).
  const talkRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (post.value === 'table') { const el = talkRef.current; if (el) el.scrollTop = el.scrollHeight; return; }
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [thread.length, busy]);
  useEffect(() => { if (card) cardRef.current?.scrollIntoView({ block: 'start' }); }, [card?.id]);
  const sayRef = useRef<HTMLTextAreaElement>(null);
  const submit = async (quick?: string) => {
    const said = (quick ?? text).trim();
    if (!said) return;
    setBusy(true); if (!quick) setText(''); awaitingDoctrine.value = false; sentThisSession = true;
    talk.value = [...talk.value, { who: 'me', text: said, at: Date.now() }];
    const r = await sendTalk(said, lang.value);
    setBusy(false);
    if (r) { talk.value = r.history; pendingDoctrine.value = cardAfterTalk(pendingDoctrine.value, r); awaitingDoctrine.value = awaitingAnswer(pendingDoctrine.value, r); if (r.policyChanged) toast.value = { text: t('compiled'), kind: 'ok' }; }
    else talk.value = [...talk.value, { who: 'general', text: t('generalOffline'), at: Date.now() }];
  };
  return (
    <div class="generalpanel">
      <h2>{t(v.me.persona as 'vane')} <small class="muted">{t(`${v.me.persona}Desc` as 'vaneDesc')}</small></h2>
      {(v.me.onboarding?.tier ?? 6) < 6 && (
        <p class="tag">{t('tierOpened').replace('{k}', t(`tier${v.me.onboarding.tier}` as 'tier1'))} · <button class="link" onClick={() => void act({ type: 'onboarding_unlock' }, t('showMeAllDone'))}>{t('showMeAll')}</button></p>
      )}
      {v.me.onboarding?.next && <p class="next-tier small" title={t('nextTier')}>{nextTierLine(nextStepParams(v.me.onboarding.next, v.time), lang.value)}</p>}
      <ExpandHelp />
      <div class="talk" ref={talkRef}>
        {thread.length === 0 && <p class="bubble general">{t('generalHello')}</p>}
        {threadItems(thread.slice(-12)).map((it) => it.kind === 'ack'
          ? <p key={it.key} class="ack" role="status">✓ {t('doctrineReceived')}</p>
          : <p key={it.key} class={`bubble ${it.turn.who}`}>{it.turn.text}</p>)}
        {busy && <p class="bubble general muted">{t('thinking')}</p>}
      </div>
      {awaiting && !card && !busy && (
        // The General asked a question about the doctrine: the question stays in view with one-tap answers (issue #47).
        <div class="awaiting" role="status">
          <small>{t('awaitingQuestion')} · {t('doctrineAwaiting')}</small>
          {lastGeneralLine(thread) && <p class="question">{lastGeneralLine(thread)}</p>}
          <div class="acts">
            <button class="primary" onClick={() => void submit(t('quickYes'))}>{t('quickYes')}</button>
            <button onClick={() => void submit(t('quickNo'))}>{t('quickNo')}</button>
            <button class="link" onClick={() => sayRef.current?.focus()}>{t('quickRephrase')}</button>
          </div>
        </div>
      )}
      <div ref={cardRef}>{card && <DoctrineCard v={v} card={card} />}</div>
      <div class={`say ${teachClass(tv, 'say')}`}>
        <textarea ref={sayRef} rows={2} value={text} placeholder={t(composerPlaceholderKey({ sentThisSession, turns: thread.length, doctrine: p.notes }))} onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} onFocus={(e) => setTimeout(() => (e.target as HTMLElement).scrollIntoView({ block: 'nearest' }), 250)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); } }} />
        <button class="primary" disabled={busy || text.trim().length < 1} onClick={() => void submit()}>{t('send')}</button>
      </div>
      <div ref={endRef} />
      {p.notes && <p class="muted small">{t('doctrine')} : {p.notes}</p>}
      <h3>{t('journalGeneral')}</h3>
      {journal.length === 0 ? <p class="muted small">{t('journalEmpty')}</p> : (
        <ul class="list journal">{journal.map((n, i) => <li key={`${n.at}-${i}`}><small>{stamp(n.at)}</small> <span>{describeNote(n, v)}</span></li>)}</ul>
      )}
      <h3>{t('policy')}</h3>
      <p>{t('expansion')}: {(p.expansion * 100).toFixed(0)} % · {t('aggression')}: {(p.aggression * 100).toFixed(0)} %</p>
      <div class="row">
        <label>{t('expansion')}<input type="range" min={0} max={1} step={0.1} value={p.expansion} onChange={(e) => void act({ type: 'set_policy', policy: { ...p, expansion: Number((e.target as HTMLInputElement).value) } })} /></label>
        <label>{t('aggression')}<input type="range" min={0} max={1} step={0.1} value={p.aggression} onChange={(e) => void act({ type: 'set_policy', policy: { ...p, aggression: Number((e.target as HTMLInputElement).value) } })} /></label>
      </div>
      <button onClick={() => void fetchBriefing(lang.value).then((b) => { if (b) briefing.value = b; })}>{t('briefing')}</button>
      <Archives persona={v.me.persona} />
    </div>
  );
}

/** The General's Archives (decision 0012): its memories found again, by arc; what is lost is counted, never named. */
function Archives({ persona }: { persona: string }) {
  const a = useSig(archivesData);
  const open = useSig(archivesOpen);
  const [read, setRead] = useState<string | null>(null);
  const shown = useSig(souvenirNow);
  useEffect(() => { void refreshArchives(); }, [shown]);
  if (!a || !a.arcs.length) return null;
  const L = lang.value;
  return (
    <details class="archives" open={open} onToggle={(e) => { archivesOpen.value = (e.target as HTMLDetailsElement).open; }}>
      <summary><h3>{t('archives')} · {t(persona as 'vane')}</h3> <small>{a.found.length} / {a.arcs.reduce((n, x) => n + x.total, 0)}</small></summary>
      {a.seance && <p class="muted small">{t('echoesHeld').replace('{n}', String(a.echoes ?? 0))}</p>}
      {(a.hints ?? []).length > 0 && (
        <ul class="list hints">{(a.hints ?? []).map((h) => <li key={h.id} class="muted small">{t('hintToFind').replace('{h}', h.hint[L])}</li>)}</ul>
      )}
      {a.arcs.map((arc) => (
        <div key={arc.arc} class="arc">
          <p class="muted small">{t(`souvenirArc${arc.arc}` as 'souvenirArc1')} · {arc.found}/{arc.total}</p>
          <ul class="list">
            {a.found.filter((s) => s.arc === arc.arc).map((s) => (
              <li key={s.id} onClick={() => setRead(read === s.id ? null : s.id)}>
                <b>{s.title[L]}</b>
                {read === s.id && <p class="souvenir-text">{s.text[L]}</p>}
              </li>
            ))}
            {arc.total > arc.found && <li class="lost muted small">{t('souvenirLost').replace('{n}', String(arc.total - arc.found))}</li>}
          </ul>
        </div>
      ))}
    </details>
  );
}

/** The General's last line in the thread: the question a doctrine waits on. */
const lastGeneralLine = (thread: readonly Turn[]): string | null => [...thread].reverse().find((x) => x.who === 'general')?.text ?? null;

/** « How do I expand? » in one tap (issue #47): the numbers come from the balance, never copied by hand. */
function ExpandHelp({ open = false }: { open?: boolean }) {
  const num = (x: number): string => x.toLocaleString(lang.value, { maximumFractionDigits: 2 });
  const p: Record<string, string> = {
    range: num(BASE_RANGE), pulsar: num(PULSAR_RANGE_MULT), amp: num(Math.round((AMPLIFIER_RANGE_MULT - 1) * 100)), decree: num(Math.round((DECREE_RANGE_MULT - 1) * 100)),
    beacon: num(BEACON_RANGE_MULT), storm: num(STORM_RANGE_MULT), sec: num(RELAY_BUILD_SECONDS_PER_UNIT), typical: num(Math.round((130 * RELAY_BUILD_SECONDS_PER_UNIT) / 60)),
    metal: num(RELAY_COST_PER_UNIT.metal), energy: num(RELAY_COST_PER_UNIT.energy),
  };
  const fill = (s: string): string => s.replace(/\{(\w+)\}/g, (_, k: string) => p[k] ?? '');
  return (
    <details class="expand-help" open={open}>
      <summary>{t('expandTitle')}</summary>
      {(['expand1', 'expand2', 'expand3', 'expand4', 'expand5'] as const).map((k) => <p key={k} class="small">{fill(t(k))}</p>)}
    </details>
  );
}

/** The Account tab (decision 0010, lot A): who I am, the devices holding this Colony, the General's memory, language,
 *  installation, and the way out. Guests are told what leaving costs; the passkey comes with lot B. */
/** The rescue e-mail (decision 0010, lot C): address, then the six-digit code, typed here, never a link. */
/** Web Push (issue #48): alerts on this device when the Colony is threatened and the game is closed. */
function PushSection({ say }: { say: (text: string, kind?: 'ok' | 'err') => void }) {
  const [cfg, setCfg] = useState<{ enabled: boolean; publicKey: string | null } | null>(null);
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { void fetchPushConfig().then(setCfg); void pushState().then(setState); }, []);
  if (!cfg?.enabled || !cfg.publicKey || !state || state === 'unsupported') return null;
  const on = async () => {
    setBusy(true);
    const ok = await enablePush(cfg.publicKey!, authHeaders()).catch(() => false);
    setBusy(false); setState(await pushState());
    say(t(ok ? 'pushOnDone' : 'pushFailed'), ok ? 'ok' : 'err');
  };
  const off = async () => { setBusy(true); await disablePush(authHeaders()).catch(() => undefined); setBusy(false); setState(await pushState()); say(t('pushOffDone')); };
  return (
    <>
      <h3>{t('pushTitle')}</h3>
      <p class="muted small">{t('pushHelp')}</p>
      {state === 'ios-install' && <p class="muted small">{t('pushIos')}</p>}
      {state === 'denied' && <p class="muted small">{t('pushDenied')}</p>}
      {state === 'off' && <button class="primary" disabled={busy} onClick={() => void on()}>{t('pushOn')}</button>}
      {state === 'on' && <p class="row"><span>{t('pushIsOn')}</span> <button disabled={busy} onClick={() => void off()}>{t('pushOff')}</button></p>}
    </>
  );
}

function EmailSection({ email, onChange, say }: { email: string | null; onChange: () => void; say: (text: string, kind?: 'ok' | 'err') => void }) {
  const [step, setStep] = useState<'idle' | 'address' | 'code'>('idle');
  const [addr, setAddr] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const l = useSig(lang);
  const send = async (e: Event) => {
    e.preventDefault(); setBusy(true); setErr(null);
    const r = await startEmail(addr.trim(), l);
    setBusy(false);
    if (r.ok) { setStep('code'); setCode(''); } else setErr(tError(r.reason ?? ''));
  };
  const confirm = async (e: Event) => {
    e.preventDefault(); setBusy(true); setErr(null);
    const r = await verifyEmail(code);
    setBusy(false);
    if (r.ok) { say(t('emailAdded')); setStep('idle'); onChange(); } else setErr(tError(r.reason ?? ''));
  };
  const drop = async () => { if (await removeEmail()) { say(t('emailRemoved')); onChange(); } };
  if (step === 'idle') {
    return (
      <>
        <p class="muted small">{t('rescueEmailHelp')}</p>
        {email ? (
          <ul class="list"><li><span><b>{email}</b></span><span class="actions"><button onClick={() => { setAddr(email); setStep('address'); }}>{t('changeEmail')}</button><button onClick={() => void drop()}>{t('emailRemove')}</button></span></li></ul>
        ) : <button class="primary" onClick={() => setStep('address')}>{t('addEmail')}</button>}
      </>
    );
  }
  return (
    <form class="selbox emailform" onSubmit={(e) => void (step === 'address' ? send(e) : confirm(e))}>
      {step === 'address' ? (
        <label>{t('emailAddress')}<input type="email" inputMode="email" autoComplete="email" value={addr} onInput={(e) => setAddr((e.target as HTMLInputElement).value)} required autoFocus /></label>
      ) : (
        <>
          <p class="muted small">{t('codeSentTo').replace('{e}', addr.trim().toLowerCase())}</p>
          <label>{t('typeCode')}<input class="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" maxLength={7} value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} required autoFocus /></label>
        </>
      )}
      {err && <p class="error">{err}</p>}
      <div class="actions">
        <button class="primary" disabled={busy || (step === 'address' ? !addr.includes('@') : code.replace(/\D/g, '').length !== 6)}>{step === 'address' ? t('sendCode') : t('confirmCode')}</button>
        {step === 'code' && <button type="button" disabled={busy} onClick={() => setStep('address')}>{t('back')}</button>}
        <button type="button" onClick={() => { setStep('idle'); setErr(null); }}>{t('cancel')}</button>
      </div>
    </form>
  );
}

/** « Soutenir Aurane » (decision 0011): a one-off purchase, a cosmetic founder title held by the account, zero effect on
 *  the game. Hidden when the server has no payment provider, unless the title is already owned. */
function SupportSection({ accountId, say }: { accountId: string | null; say: (text: string, kind?: 'ok' | 'err') => void }) {
  const l = useSig(lang);
  const ret = useSig(paidReturn);
  const [cfg, setCfg] = useState<PaymentsConfig | null>(null);
  const [ent, setEnt] = useState<Awaited<ReturnType<typeof fetchEntitlements>>>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { void fetchPublicConfig().then((c) => setCfg(c.payments ?? { enabled: false, skus: [] })); }, []);
  // The webhook may land a few seconds after Stripe sends the player back: poll until the title exists (30 s at most).
  useEffect(() => {
    let stop = false; let tries = 0;
    const load = async (): Promise<void> => {
      const e = await fetchEntitlements();
      if (stop) return;
      setEnt(e);
      const has = e?.entitlements.some((x) => x.sku === 'support_founder' && x.active) ?? false;
      if (!has && ret && ret !== 'cancel' && tries++ < 15) setTimeout(() => void load(), 2000);
    };
    void load();
    return () => { stop = true; };
  }, [ret, accountId]);
  const owned = ent?.entitlements.some((x) => x.sku === 'support_founder' && x.active) ?? false;
  const offer = cfg?.enabled ? cfg.skus.find((x) => x.id === 'support_founder') : undefined;
  const back = ret !== null && ret !== 'cancel';
  if (!owned && !offer && !back) return null;
  const price = offer ? String(offer.priceChf) : '5';
  const buy = async () => {
    setBusy(true);
    const r = await startCheckout('support_founder', l);
    setBusy(false);
    say(t('supportFailed').replace('{r}', tError(r.reason)), 'err');
  };
  return (
    <>
      <h3>{t('supportTitle')}</h3>
      {owned ? (
        <>
          <p><span class="tag ok founder">✦ {t('founderTitle')}</span></p>
          <p class="muted small">{back ? t('supportThanks') : t('supportOwned')}</p>
        </>
      ) : back ? (
        <p class="muted small">{t('supportPending')}</p>
      ) : (
        <>
          {ret === 'cancel' && <p class="muted small">{t('supportCancelled')}</p>}
          <p class="muted small">{t('supportText').replace('{p}', price)}</p>
          <p class="muted small">{t('supportLegal')}</p>
          {ent && !ent.account ? <p class="muted small">{t('supportNeedsAccount')}</p> : <button class="primary" disabled={busy || !ent} onClick={() => void buy()}>{t('supportButton').replace('{p}', price)}</button>}
        </>
      )}
    </>
  );
}

function AccountPanel({ v }: { v: PlayerView }) {
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [keying, setKeying] = useState(false);
  const l = useSig(lang);
  const load = () => { void fetchSessions().then(setSessions); void fetchAccount().then(setAccount); };
  useEffect(load, []);
  const hasKeys = (account?.passkeys.length ?? 0) > 0;
  const hasEmail = !!account?.account?.email;
  const protectedBy = hasKeys || hasEmail;
  const say = (text: string, kind: 'ok' | 'err' = 'ok') => { toast.value = { text, kind }; setTimeout(() => { if (toast.value?.text === text) toast.value = null; }, 3000); };
  const enroll = async () => {
    setKeying(true);
    const r = await addPasskey(l);
    setKeying(false);
    if (r.ok) { say(t('passkeyAdded')); load(); }
    else if (r.reason === 'cancelled') say(t('passkeyCancelled'), 'err');
    else say(t('passkeyFailed').replace('{r}', r.reason ?? ''), 'err');
  };
  const drop = async (id: string) => { if (await removePasskey(id)) load(); };
  const when = (ms: number | null): string => (ms ? new Date(ms).toLocaleString(l === 'fr' ? 'fr-CH' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) : t('neverSeen'));
  const cut = async (id: string) => { if (await revokeSession(id)) { toast.value = { text: t('cutOffDone'), kind: 'ok' }; setTimeout(() => { toast.value = null; }, 2500); load(); } };
  const download = async () => {
    const data = await exportMemory();
    if (data === null) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `aurane-memory-${v.me.id}.json`; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };
  const erase = async () => { setErasing(false); if (await eraseMemory()) { toast.value = { text: t('erased'), kind: 'ok' }; setTimeout(() => { toast.value = null; }, 2500); } };
  return (
    <div class="account">
      <h2>{v.me.name} <small class={`f-${v.me.faction}`}>{t(v.me.faction as 'guild')}</small></h2>
      <p class="muted">{t(v.me.persona as 'vane')} · <span class={`tag ${protectedBy ? 'ok' : ''}`}>{protectedBy ? t('accountProtected') : t('accountGuest')}</span></p>
      <p class="muted small">{hasKeys ? t('accountProtectedHelp') : hasEmail ? t('accountProtectedEmail') : t('accountGuestHelp')}</p>
      <h3>{t('passkeys')} {account && account.passkeys.length > 0 && <small>{account.passkeys.length}</small>}</h3>
      {!protectedBy && <p class="muted small">{t('addPasskeyHelp')}</p>}
      <ul class="list">
        {(account?.passkeys ?? []).map((k) => (
          <li key={k.id}>
            <span><b>{k.label || '—'}</b> <small>· {t('lastUsed')} {when(k.lastUsedAt)}</small></span>
            <button onClick={() => void drop(k.id)}>{t('removePasskey')}</button>
          </li>
        ))}
      </ul>
      {passkeysSupported() ? <button class="primary" disabled={keying} onClick={() => void enroll()}>{t('addPasskey')}</button> : <p class="muted small">{t('passkeyUnsupported')}</p>}
      <h3>{t('rescueEmail')}</h3>
      <EmailSection email={account?.account?.email ?? null} onChange={load} say={say} />
      {account?.account?.email && <label class="check"><input type="checkbox" checked={account.account.notifyEmail === true} onChange={(e) => { const on = (e.target as HTMLInputElement).checked; void setNotifyEmail(on).then((ok) => { if (ok) { say(t(on ? 'notifyOn' : 'notifyOff')); load(); } }); }} /> {t('notifyEmail')}</label>}
      <PushSection say={say} />
      <SupportSection accountId={account?.account?.id ?? null} say={say} />
      <h3>{t('devices')} {sessions && <small>{sessions.length}</small>}</h3>
      <ul class="list">
        {(sessions ?? []).map((s) => (
          <li key={s.id}>
            <span><b>{s.label || '—'}</b>{s.current ? <span class="tag"> {t('thisDevice')}</span> : null} <small>· {t('lastSeen')} {when(s.lastSeenAt ?? s.createdAt)}</small></span>
            {!s.current && <button onClick={() => void cut(s.id)}>{t('cutOff')}</button>}
          </li>
        ))}
      </ul>
      <DeviceLink />
      <h3>{t('memoryTitle')}</h3>
      <p class="muted small">{t('memoryHelp')}</p>
      <div class="actions">
        <button onClick={() => void download()}>{t('exportMemory')}</button>
        {!erasing ? <button onClick={() => setErasing(true)}>{t('eraseMemory')}</button> : <><span class="bad small">{t('eraseMemoryConfirm')}</span> <button class="primary" onClick={() => void erase()}>{t('eraseMemory')}</button> <button onClick={() => setErasing(false)}>{t('cancel')}</button></>}
      </div>
      <h3>{t('language')}</h3>
      <div class="lang"><button class={l === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>FR</button><button class={l === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button></div>
      <div class="actions"><InstallButton compact /><a class="link" href={`/c/${encodeURIComponent(v.me.id)}`} target="_blank" rel="noopener">{t('colonyPage')} ›</a></div>
      <h3>{t('logout')}</h3>
      {!leaving ? <button onClick={() => setLeaving(true)}>{t('logout')}</button> : protectedBy ? (
        <div class="selbox">
          <p class="muted">{hasKeys ? t('logoutSafe') : t('logoutSafeEmail')}</p>
          <div class="actions"><button class="primary" onClick={() => void logout()}>{t('logout')}</button><button onClick={() => setLeaving(false)}>{t('cancel')}</button></div>
        </div>
      ) : (
        <div class="selbox">
          <p class="bad">{t('logoutWarn')}</p>
          <div class="actions">
            {passkeysSupported() && <button class="primary" disabled={keying} onClick={() => void enroll()}>{t('addPasskey')}</button>}
            <button onClick={() => void logout()}>{t('logoutAnyway')}</button>
            <button onClick={() => setLeaving(false)}>{t('cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A 24 h link to open this colony on another device (phone, laptop). */
function DeviceLink() {
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  return (
    <div class="devicelink">
      {!url && <button onClick={() => void requestLink().then((r) => setUrl(r?.url ?? null))}>{t('linkDevice')}</button>}
      {url && (
        <>
          <p class="muted">{t('linkDeviceHelp')}</p>
          <input readOnly value={url} onFocus={(e) => (e.target as HTMLInputElement).select()} />
          <button onClick={() => { void navigator.clipboard?.writeText(url).then(() => setCopied(true)); }}>{copied ? t('copied') : t('copy')}</button>
        </>
      )}
    </div>
  );
}

const LOG_READ_KEY = 'aurane.logread';
const logRead = signal<number>((() => { try { return Number(localStorage.getItem(LOG_READ_KEY) ?? -1); } catch { return -1; } })());
function markLogRead(v: PlayerView): void {
  const last = v.events.length ? v.events[v.events.length - 1]!.at : v.time;
  if (last <= logRead.value) return;
  logRead.value = last;
  try { localStorage.setItem(LOG_READ_KEY, String(last)); } catch { /* ignore */ }
}

/** The living log: one readable line per event, the hourly recap as a card, newest first. */
function LogPanel({ v }: { v: PlayerView }) {
  const lines = v.events.map((e, i) => describeEvent(e, v, i)).filter((x): x is NonNullable<typeof x> => x !== null).reverse();
  const tv = useSig(teach);
  const firstRecap = lines.find((l) => l.recap)?.key;
  if (!lines.length) return <p class="muted">{t('noEvents')}</p>;
  return (
    <ul class="list feed">
      {lines.map((l) => (
        <li key={l.key} class={`tone-${l.tone} ${l.system ? 'has-sys' : ''}`} onClick={l.system ? () => { selected.value = l.system; } : undefined}>
          {l.recap ? (
            <div class={`recap ${l.key === firstRecap ? teachClass(tv, 'recap') : ''}`}>
              <div class="head"><b>{l.text}</b> <small>{stamp(l.at)}</small></div>
              <div class="chips">
                {RES.map((r) => <span key={r} class={`chip r-${r}`}><Icon name={r} size={12} /> <b>+{fmt(l.recap!.produced[r] ?? 0)}</b></span>)}
                <span class="chip r-credits"><Icon name="credits" size={12} /> <b>+{l.recap.credits}</b></span>
              </div>
              <small class="muted">{l.recap.productive} {t('recapSystems')} · {l.recap.drawn} {t('recapDrawn')}{l.recap.overflow > 0 ? <> · <span class="bad">{l.recap.overflow} {t('recapLost')}</span></> : null}{l.recap.unpowered > 0 ? <> · <span class="bad">{l.recap.unpowered} {t('recapUnpowered')}</span></> : null}</small>
              {l.recap.objective && <small class={`objective ${l.recap.objective.done ? 'ok' : 'bad'}`}>{t('objective')} · {objectiveText(l.recap.objective)} : <b>{l.recap.objective.done ? t('objDone') : t('objMissed')}</b></small>}
            </div>
          ) : (
            <><i /> <span>{l.text}</span> <small>{stamp(l.at)}</small></>
          )}
        </li>
      ))}
    </ul>
  );
}
